import { TWO_ROOM_DEFAULT_DIMENSIONS } from "./defaultDimensions";
import { dividerHeaterCenterZM } from "../domain/partition/partitionSplit";
import { type ChimneyBuildInput, chimneyStackFor } from "./chimneyInput";
import type { FlueAxisM } from "./squareBaseBuilder/context";
import { createPartitionFramePlan } from '../domain/partition/partitionFramePlan';
import * as THREE from "three";
import {
	type BuildGuideDictionaryKey,
	type BuildGuideText,
	type BuildGuideModifierContext,
	type BuildGuideSubstepDraft,
	buildGuideText,
	insertSubstepsAfterId,
	type ProjectBuildGuideStepDraft,
} from "../domain/build/buildGuideDraft";
import { calculateHeaterPlacement } from "../domain/chimney/calculateHeaterPlacement";
import { calculateFoundation } from "../domain/foundation/calculateFoundation";
import {
	DEFAULT_SITE_CONDITIONS,
	type SiteConditions,
} from "../domain/foundation/siteConditions";
import {
	type FramedWallResult,
	type GlazingZone,
	generateFramedWall,
	type WallOpening,
} from "../domain/framing/generateFramedWall";
import {
	type BenchAnchor,
	type BenchRun,
	createInteriorLayout,
	type HeaterPlacement,
	type HeaterSizeMm,
	type InteriorLayoutVariantId,
	type InteriorRoom,
	type InteriorWall,
	reconcileInteriorLayout,
} from "../domain/interior";
import {
	RELAX_BENCH_DEPTH_MM,
	RELAX_BENCH_END_MARGIN_MM,
	RELAX_BENCH_LEG_HEIGHT_MM,
	RELAX_BENCH_WALL_OFFSET_MM,
} from "../domain/interior/constants";
import type { BenchMaterialsResult } from "../domain/interior/deriveBenchMaterials";
import { buildInteriorPanelCutRow } from "../domain/finishes/interiorPanelCuts";
import {
	DEFAULT_FINISHES,
	type FinishConfig,
	type InteriorPanelDirection,
} from "../domain/finishes/types";
import { calculatePartitionWallFraming } from "../domain/partition/calculatePartitionWallFraming";
import { clampPartitionSplit } from "../domain/partition/partitionSplit";
import { createRectRoofGeometry } from "../domain/roof/rectRoofGeometry";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import type { RoofJoistResult } from "../domain/snowzone/calculateRoofJoists";
import {
	DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
	DEFAULT_DIVIDER_DOOR_WIDTH_MM,
	getCenteredDividerDoorX,
} from "../pages/builder/defaultOpenings";
import { createCameraPosition, createCameraPreset } from "./cameraFraming";
import {
	ENGINE_CC_M as CC,
	ENGINE_INTERIOR_BATTEN_CC_M,
	ENGINE_INTERIOR_BATTEN_D_M,
	ENGINE_INTERIOR_BATTEN_W_M,
	ENGINE_JOIST_H_M as JOIST_H,
	ENGINE_STUD_D_M as STUD_D_M,
	ENGINE_STUD_W_M as STUD_W,
	ENGINE_CLADDING_COV_M,
} from "./constants";
import { getDeckSuppressedWalls } from "./deckGeometry";
import { addOpeningTrimFrame } from "./detailHelpers";
import { addVentPassageExterior, cutVentPassages, fitVentBetweenStuds, type VentPassage } from "./ventPassages";
import { DirtyFlag } from "./dirtyTracking";
import { calculateVentilatedFacadeQuantities } from "./facadeQuantities";
import {
	createFrontVisibilityGroup,
	getFrontVisibilityGroup,
	createDividerVisibilityGroup,
	type FrontVisibilityGroups,
} from "./frontVisibility";
import { addBox, addSlopedBox } from "./geometry";
import {
	getHeaterDepth,
	getHeaterHeight,
	getHeaterSizeMm,
	getHeaterWidth,
} from "./heaterGeometry";
import { calculateInteriorLiningQuantities } from "./interiorQuantities";
import {
	createInteriorRenderPlan,
	heaterDoorRotationY,
	type InteriorRenderPlan,
} from "./interiorRenderPlan";
import {
	matCornerTrim,
	matCornerTrimHorizontal,
	matDivider,
	matDoorHandle,
	matDoorWood,
	matGlass,
	matWood,
} from "./materials";
import {
	getTwoRoomAuditMetadata,
	getTwoRoomEvidenceConfig,
} from "./rectangularAudit";
import {
	getFloorJoistDimensionLabel,
	getFloorJoistPriceRuleId,
	getRoofJoistCutDimensionLabel,
	getRoofJoistDepthM,
	getRoofJoistDimensionLabel,
	getRoofJoistMaterialName,
	getRoofJoistPriceRuleId,
	getRoofJoistCutPurchase,
	getStructuralRoofJoists,
	resolveAdvisoryRoofJoists,
} from "./roofJoistSizing";
import {
	addProjectedWallBox,
	buildExteriorFacade,
	buildFloorJoists,
	buildFoundation,
	buildInteriorCeilingFinish,
	buildInteriorCeilingLayers,
	buildPanels,
	buildRoof,
	buildSaunaRoomCore,
	buildWallFraming,
	createSquareBaseBuildContext,
	getFrontDoorCenterX,
	type SquareBaseBuildContext,
	type WallFramingResult,
	wallHeightAtAxis,
} from "./squareBaseBuilder";
import {
	resolveSubfloorBattThicknessMm,
	resolveSubfloorBayCount,
	resolveSubfloorLedgerLm,
} from "./subfloor";
import {
	getTwoRoomHeaterWall,
	getTwoRoomLongEntryWall,
	getViewLayoutHeaterCenter,
	isTwoRoomViewLayout,
	TWO_ROOM_DEFAULT_ENTRY_WALL,
	VIEW_LAYOUT_BENCH_DOOR_GAP_MM,
	TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM,
	TWO_ROOM_PANORAMA_WALL,
	type TwoRoomFramingWall,
} from "./twoRoomConstants";
import {
	resolveOpeningAdjustmentOpenings,
} from "./twoRoomOpeningConfig";
import type {
	BuildInput,
	BuildResult,
	CalculationResult,
	CutRow,
	HeaterType,
	MaterialRow,
	RawCalculationResult,
	RawSaunaEngine,
	RoofConfig,
	TwoRoomConfig,
} from "./types";
import {
	type HeaterRef,
	heaterKindOf,
	heaterModelIdOf,
	resolveWallHeightM,
} from "../domain/geometry/buildingDimensions";

export function resolveTwoRoomConfig(
	input: Partial<TwoRoomConfig> = {},
): TwoRoomConfig {
	return {
		entryWall: input.entryWall ?? TWO_ROOM_DEFAULT_ENTRY_WALL,
		glazingStyle: input.glazingStyle ?? "standard",
		saunaPosition: input.saunaPosition ?? "left",
		hasRelaxRoom: input.hasRelaxRoom ?? true,
	};
}

export function resolvePartitionSplit(
	split: number | undefined,
	lengthM: number,
	hasRelaxRoom = true,
): number {
	// Without a relax room there is no divider, so the relax-aware buildable
	// bounds don't apply — keep the legacy clamp (split only feeds panel BOM).
	if (!hasRelaxRoom) {
		return Math.min(0.8, Math.max(0.35, split ?? 0.5));
	}
	return clampPartitionSplit(lengthM, split ?? 0.5);
}

/** Stud width in mm — the gap reserved for the partition mullion. */
const TWO_ROOM_STUD_W_MM = Math.round(STUD_W * 1000);

/**
 * Local X (mm) of the divider where it meets a long wall. Local X runs from
 * 0 at the back wall (-L/2) to lengthMm at the front wall (+L/2), matching the
 * convention used for right-wall openings in {@link getRelaxWindowX}.
 */
export function getDividerLocalXMm(
	lengthMm: number,
	split: number,
	config: TwoRoomConfig,
): number {
	return Math.round(
		config.saunaPosition === "right"
			? lengthMm * (1 - split)
			: lengthMm * split,
	);
}

export interface TwoRoomRoomLayout {
	hasRelaxRoom: boolean;
	saunaPosition: TwoRoomConfig["saunaPosition"];
	dividerZ: number;
	saunaMinZ: number;
	saunaMaxZ: number;
	saunaCenterZ: number;
	saunaLength: number;
	relaxMinZ: number;
	relaxMaxZ: number;
	relaxCenterZ: number;
	relaxLength: number;
	saunaDirection: 1 | -1;
	/** One room laid out to face the panorama glass. See `isTwoRoomViewLayout`. */
	viewLayout: boolean;
}

/**
 * Which wall the hot room's lavar hang on, and how far off it they sit.
 *
 * ONE owner for a decision three systems make: the renderer positions the
 * bench meshes from it, the clearance check builds its obstacle boxes from it,
 * and the interior layout generates its default runs from it. When the resolver
 * assumed "always the back wall", a right-positioned two-room generated its
 * benches against the DIVIDER while the model drew them at the far end — an
 * under-sized bench BOM and wrong seating capacity for a supported layout.
 *
 * The rule: benches go at the sauna's far end from the divider, which is the
 * back wall when the room runs +Z and the front wall when it runs −Z, flush
 * against it. The upper lav hangs on that wall; it used to stand 100 mm off
 * it, which made `resolveBenchSupport` call it freestanding and carry it on
 * two rows of full-height posts, the "long thin legs" seen through the glass.
 */
export interface HotRoomBenchPlacement extends BenchAnchor {
	/** The generated layout's variant: the view layout's is an L. */
	variant: InteriorLayoutVariantId;
}

export function getHotRoomBenchPlacement(
	roomLayout: TwoRoomRoomLayout,
	/**
	 * The openings the engine frames and the building's outer length. Only the
	 * view layout reads them: its lavar share the entry wall with the door and
	 * must stop short of it.
	 */
	door?: { openings: readonly WallOpening[]; lengthM: number },
): HotRoomBenchPlacement {
	if (roomLayout.viewLayout) {
		// The L faces the glass: main band along the entry wall, both tiers
		// turning the corner onto the back wall.
		const entryDoor = door?.openings.find(
			(opening) =>
				opening.type === "door" &&
				opening.wall === getTwoRoomLongEntryWall({ glazingStyle: "panorama" }),
		);
		const doorStartMm = entryDoor
			? -(door?.lengthM ?? 0) * 500 +
				entryDoor.xMm -
				roomLayout.saunaMinZ * 1000
			: undefined;
		return {
			wall: getTwoRoomLongEntryWall({ glazingStyle: "panorama" }),
			offsetFromWallMm: 0,
			endMm:
				doorStartMm === undefined
					? undefined
					: Math.max(0, doorStartMm - VIEW_LAYOUT_BENCH_DOOR_GAP_MM),
			wrapTiers: true,
			variant: "l-shape",
		};
	}
	if (!roomLayout.hasRelaxRoom) {
		return { wall: "back", offsetFromWallMm: 0, variant: "two-tier" };
	}
	return {
		wall: roomLayout.saunaDirection === 1 ? "back" : "front",
		offsetFromWallMm: 0,
		variant: "two-tier",
	};
}

/**
 * The relax room's bench, in the same wall-relative terms as a `BenchRun`.
 *
 * ONE owner, for the same reason as `getHotRoomBenchPlacement`: the renderer
 * positions the mesh from it and the layout generates its default run from it.
 * Before this existed the bench was drawn but never bought — the material list
 * quoted a build that was missing a bench the guide tells you to make.
 */
export interface RelaxBenchPlacement {
	wall: "left" | "right";
	startMm: number;
	lengthMm: number;
	depthMm: number;
	offsetFromWallMm: number;
	legHeightMm: number;
}

export function getRelaxBenchPlacement(
	roomLayout: TwoRoomRoomLayout,
	config: TwoRoomConfig,
): RelaxBenchPlacement | null {
	if (!roomLayout.hasRelaxRoom) return null;
	const relaxLengthMm = roomLayout.relaxLength * 1000;
	const lengthMm = Math.max(200, relaxLengthMm - RELAX_BENCH_END_MARGIN_MM);
	return {
		// Sits on the wall opposite the long-wall entry; under panorama that is
		// the glass wall, so the bench faces the view.
		wall: getTwoRoomLongEntryWall(config) === "right" ? "left" : "right",
		startMm: (relaxLengthMm - lengthMm) / 2,
		lengthMm,
		depthMm: RELAX_BENCH_DEPTH_MM,
		offsetFromWallMm: RELAX_BENCH_WALL_OFFSET_MM,
		legHeightMm: RELAX_BENCH_LEG_HEIGHT_MM,
	};
}

interface TwoRoomEntryPlacement {
	wall: "front" | "left" | "right";
	centerM: number;
}

interface TwoRoomScenePlan {
	config: TwoRoomConfig;
	roomLayout: TwoRoomRoomLayout;
	sceneOpenings: WallOpening[];
	glazingZonesByWall: Partial<
		Record<"front" | "back" | "left" | "right", GlazingZone[]>
	>;
	entryPlacement: TwoRoomEntryPlacement;
}

export function createTwoRoomRoomLayout(
	ctx: SquareBaseBuildContext,
	split: number,
	config: TwoRoomConfig,
): TwoRoomRoomLayout {
	if (!config.hasRelaxRoom) {
		const saunaMinZ = -ctx.L / 2 + ctx.sD + ctx.iL + ctx.pT;
		const saunaMaxZ = ctx.L / 2 - ctx.sD - ctx.iL - ctx.pT;
		const saunaLength = saunaMaxZ - saunaMinZ;
		return {
			hasRelaxRoom: false,
			saunaPosition: config.saunaPosition,
			dividerZ: 0,
			saunaMinZ,
			saunaMaxZ,
			saunaCenterZ: (saunaMinZ + saunaMaxZ) / 2,
			saunaLength,
			relaxMinZ: 0,
			relaxMaxZ: 0,
			relaxCenterZ: 0,
			relaxLength: 0,
			saunaDirection: 1,
			viewLayout: isTwoRoomViewLayout(config),
		};
	}

	const saunaLength = ctx.L * split;
	if (config.saunaPosition === "right") {
		const saunaMaxZ = ctx.L / 2 - ctx.sD - ctx.iL - ctx.pT;
		const saunaMinZ = ctx.L / 2 - saunaLength;
		const relaxMinZ = -ctx.L / 2 + ctx.sD + ctx.iL + ctx.pT;
		const relaxMaxZ = saunaMinZ;
		return {
			hasRelaxRoom: true,
			saunaPosition: config.saunaPosition,
			dividerZ: saunaMinZ,
			saunaMinZ,
			saunaMaxZ,
			saunaCenterZ: (saunaMinZ + saunaMaxZ) / 2,
			saunaLength: saunaMaxZ - saunaMinZ,
			relaxMinZ,
			relaxMaxZ,
			relaxCenterZ: (relaxMinZ + relaxMaxZ) / 2,
			relaxLength: relaxMaxZ - relaxMinZ,
			saunaDirection: -1,
			viewLayout: false,
		};
	}

	const saunaMinZ = -ctx.L / 2 + ctx.sD + ctx.iL + ctx.pT;
	const saunaMaxZ = -ctx.L / 2 + saunaLength;
	const relaxMinZ = saunaMaxZ;
	const relaxMaxZ = ctx.L / 2 - ctx.sD - ctx.iL - ctx.pT;
	return {
		hasRelaxRoom: true,
		saunaPosition: config.saunaPosition,
		dividerZ: saunaMaxZ,
		saunaMinZ,
		saunaMaxZ,
		saunaCenterZ: (saunaMinZ + saunaMaxZ) / 2,
		saunaLength: saunaMaxZ - saunaMinZ,
		relaxMinZ,
		relaxMaxZ,
		relaxCenterZ: (relaxMinZ + relaxMaxZ) / 2,
		relaxLength: relaxMaxZ - relaxMinZ,
		saunaDirection: 1,
		viewLayout: false,
	};
}

function getExteriorEntryPlacement(
	ctx: SquareBaseBuildContext,
	sceneOpenings: WallOpening[],
	config: TwoRoomConfig,
): TwoRoomEntryPlacement {
	if (config.entryWall === "long") {
		const longWall = getTwoRoomLongEntryWall(config);
		const longDoor = sceneOpenings.find(
			(opening) => opening.wall === longWall && opening.type === "door",
		);
		if (longDoor) {
			return {
				wall: longWall,
				centerM: -ctx.L / 2 + longDoor.xMm / 1000 + longDoor.widthMm / 2000,
			};
		}
	}

	return {
		wall: "front",
		centerM: getFrontDoorCenterX(ctx, sceneOpenings),
	};
}

function createTwoRoomGlazingZonesByWall(
	ctx: SquareBaseBuildContext,
	config: TwoRoomConfig,
	split: number,
): TwoRoomScenePlan["glazingZonesByWall"] {
	const zones = getGlazingZonesForWall(
		TWO_ROOM_PANORAMA_WALL,
		Math.round(ctx.L * 1000),
		config,
		split,
	);
	return zones.length > 0 ? { [TWO_ROOM_PANORAMA_WALL]: zones } : {};
}

function resolveTwoRoomScenePlan(
	input: BuildInput,
	ctx: SquareBaseBuildContext,
): TwoRoomScenePlan {
	const config = resolveTwoRoomConfig(input);
	const split = resolvePartitionSplit(
		input.partitionSplit,
		input.dimensions.length,
		config.hasRelaxRoom,
	);
	const sceneInput = { ...input, ...config };
	const sceneOpenings = resolveOpeningAdjustmentOpenings(sceneInput);
	return {
		config,
		roomLayout: createTwoRoomRoomLayout(ctx, split, config),
		sceneOpenings,
		glazingZonesByWall: createTwoRoomGlazingZonesByWall(ctx, config, split),
		entryPlacement: getExteriorEntryPlacement(ctx, sceneOpenings, config),
	};
}

function buildTwoRoomWallFraming(
	ctx: SquareBaseBuildContext,
	scenePlan: TwoRoomScenePlan,
	framingGroup = new THREE.Group(),
	frontFramingGroup = new THREE.Group(),
) {
	return buildWallFraming(
		ctx,
		scenePlan.sceneOpenings,
		framingGroup,
		frontFramingGroup,
		{
			namePrefix: "two-room",
			glazingZonesByWall: scenePlan.glazingZonesByWall,
		},
	);
}

interface TwoRoomFramingWallPlan {
	wall: TwoRoomFramingWall;
	standardStudCount: number;
	/**
	 * Unique stud axis positions (m from the wall's local origin) across all
	 * stud types — the renderer draws one ströläkt run per entry, as tall as
	 * the wall is at that x.
	 */
	studLinePositionsM: number[];
 standardCutLengthsMm:number[];
	requiresLateralSupport: boolean;
}

interface TwoRoomFramingPlan {
	walls: TwoRoomFramingWallPlan[];
	totalExteriorStudLm: number;
	extraWallStudLm: number;
	extraCuts: CutRow[];
	extraMaterials: MaterialRow[];
	lateralSupportCount: number;
}

function getGlazingZonesForWall(
	wall: TwoRoomFramingWall,
	lengthMm: number,
	config: TwoRoomConfig,
	split: number,
): GlazingZone[] {
	if (wall !== TWO_ROOM_PANORAMA_WALL || config.glazingStyle !== "panorama") {
		return [];
	}

	const yMm = 300;
	const heightMm = TWO_ROOM_PANORAMA_GLAZING_HEIGHT_MM;

	// One-room panorama has no partition, so the glass spans the whole wall.
	if (!config.hasRelaxRoom) {
		return [
			{
				id: "panorama-right-wall",
				xStartMm: 0,
				xEndMm: lengthMm,
				yMm,
				heightMm,
			},
		];
	}

	// Split the glazing at the partition line and leave a stud-wide gap so the
	// mellanvägg meets a real mullion stud instead of bisecting a glass pane.
	const dividerXMm = getDividerLocalXMm(lengthMm, split, config);
	const halfGapMm = Math.round(TWO_ROOM_STUD_W_MM / 2);
	const backEndMm = dividerXMm - halfGapMm;
	const frontStartMm = dividerXMm + halfGapMm;
	const zones: GlazingZone[] = [];
	if (backEndMm > 10) {
		zones.push({
			id: "panorama-right-wall-1",
			xStartMm: 0,
			xEndMm: backEndMm,
			yMm,
			heightMm,
		});
	}
	if (lengthMm - frontStartMm > 10) {
		zones.push({
			id: "panorama-right-wall-2",
			xStartMm: frontStartMm,
			xEndMm: lengthMm,
			yMm,
			heightMm,
		});
	}
	return zones;
}


/**
 * Panorama zone cripple cuts, derived from the SAME framed result the
 * renderer draws — never re-simulated. A parallel count from the glazing
 * top alone kept emitting "Panorama överstöd" rows after the domain moved
 * top cripples to the header TOP (and dropped them entirely when the deep
 * panorama header fills the space under the top plate).
 */
function addPanoramaCrippleCuts(
	cuts: CutRow[],
	zone: GlazingZone,
	framed: FramedWallResult,
	wall: TwoRoomFramingWall,
): number {
	const zoneStuds = framed.studs.filter(
		(stud) => stud.xMm > zone.xStartMm && stud.xMm < zone.xEndMm,
	);
	let totalLm = 0;
	for (const [type, part] of [
		["cripple_bottom", "understöd"],
		["cripple_top", "överstöd"],
	] as const) {
		const cripples = zoneStuds.filter((stud) => stud.type === type);
		if (cripples.length === 0) continue;
		// All cripples of one type in a zone share the same length.
		const lengthMm = cripples[0].yEndMm - cripples[0].yStartMm;
		cuts.push({
			part: `Panorama ${part} (${wall})`,
			count: `${cripples.length} st`,
			cutLength: `${lengthMm} mm`,
		});
		totalLm += (cripples.length * lengthMm) / 1000;
	}
	return totalLm;
}

function calculateTwoRoomFramingPlan(
	W: number,
	L: number,
	wallHeights: Record<TwoRoomFramingWall, number>,
	openings: WallOpening[],
	split: number,
	config: TwoRoomConfig,
 heightAt:(wall:TwoRoomFramingWall,axisM:number)=>number,
): TwoRoomFramingPlan {
	const widthMm = Math.round(W * 1000);
	const lengthMm = Math.round(L * 1000);
	const structuralOpenings = resolveOpeningAdjustmentOpenings({dimensions:{width:W,length:L},openings,partitionSplit:split,...config,thermalInputs:{heaterType:'wood',glassAreaSqm:0,stoneAreaSqm:0,isOutdoorLogCabin:false,includesShower:false}});

	const extraCuts: CutRow[] = [];
	const extraMaterials: MaterialRow[] = [];
	let extraWallStudLm = 0;
	const walls: TwoRoomFramingWallPlan[] = (
		["front", "back", "left", "right"] as const
	).map((wall) => {
		const wallLengthMm =
			wall === "front" || wall === "back" ? widthMm : lengthMm;
		const wallHeightMm = Math.round(wallHeights[wall] * 1000);
		const wallOpenings = structuralOpenings.filter(
			(opening) => opening.wall === wall,
		);
		const glazingZones = getGlazingZonesForWall(
			wall,
			wallLengthMm,
			config,
			split,
		);
    const frameInput={wallLengthMm,wallHeightMm,openings:wallOpenings,glazingZones};
    let framed:FramedWallResult;
    try {framed=generateFramedWall(frameInput);} catch {framed=generateFramedWall({...frameInput,openings:[]});}

		const standardStuds = framed.studs.filter(
			(stud) => stud.type === "standard",
		);
    const standardCutLengthsMm = standardStuds.map(stud=>Math.round(Math.min(stud.yEndMm,heightAt(wall,stud.xMm/1000)*1000-45)-stud.yStartMm)).filter(n=>n>0);
    const standardStudCount = standardCutLengthsMm.length;
		// The renderer draws one standing ströläkt run per UNIQUE stud axis
		// position across ALL stud types (king/jack studs at every opening edge,
		// panorama mullions), deduped by x in renderStudAlignedStrips, and each
		// run is as tall as the wall is at that x. Mirror the axis list here so
		// the exterior-facade BOM (ströläkt lpm) measures the same runs against
		// the same roof profile — a count alone can't, since a gable end wall's
		// centre runs reach the ridge while its corner runs stop at the eave.
		const studLinePositionsM = [
			...new Set(
				framed.studs
					.filter((stud) => stud.yEndMm > stud.yStartMm)
					.map((stud) => stud.xMm / 1000),
			),
		].sort((a, b) => a - b);
		for (const zone of glazingZones) {
			extraWallStudLm += addPanoramaCrippleCuts(extraCuts, zone, framed, wall);
		}
		// Partition mullion where the divider meets the panorama wall — a real
		// full-height frame member so the glass splits cleanly around it.
		if (
			wall === TWO_ROOM_PANORAMA_WALL &&
			config.glazingStyle === "panorama" &&
			config.hasRelaxRoom &&
			glazingZones.length > 1
		) {
			const mullionLengthMm = wallHeightMm - 2 * TWO_ROOM_STUD_W_MM;
			extraWallStudLm += mullionLengthMm / 1000;
			extraCuts.push({
				part: `Panorama mittregel (${wall})`,
				count: "1 st",
				cutLength: `${mullionLengthMm} mm`,
				materialKey: "framing.panorama-mullion",
			});
			extraMaterials.push({
				material: `Panorama mittregel (${wall})`,
				dimension: "45×95 mm",
				quantity: "1 st",
				phase: "framing",
				priceRuleId: "prices.vaggreglar",
				materialKey: "framing.panorama-mullion",
			});
		}
		for (const header of framed.headers) {
			if (!glazingZones.some((zone) => zone.id === header.openingId)) {
				continue;
			}
			const headerLengthMm = header.xEndMm - header.xStartMm;
			const headerLengthM = headerLengthMm / 1000;
			extraMaterials.push({
				material: `Panoramaavväxling (${wall})`,
				dimension: `45×${header.depthMm} mm`,
				quantity: `${Number(headerLengthM.toFixed(2))} lpm`,
				phase: "framing",
				priceRuleId:
					header.depthMm >= 220
						? "prices.takbjalkar45x220"
						: "prices.kortlingRegel",
				materialKey: "framing.panorama-header",
			});
			extraCuts.push({
				part: `Header ${header.depthMm}mm (${wall})`,
				count: "1 st",
				cutLength: `${headerLengthMm} mm`,
        purchaseDimension: `45x${header.depthMm}`,
        pieces:[{lengthMm:headerLengthMm,count:1}],
			});
		}

		return {
			wall,
			standardStudCount,
      standardCutLengthsMm,
			studLinePositionsM,
			requiresLateralSupport: framed.requiresLateralSupport,
		};
	});
	const totalExteriorStudLm = walls.reduce(
		(sum, wall) => sum + wall.standardCutLengthsMm.reduce((s,n)=>s+n/1000,0),
		0,
	);

	return {
		walls,
		totalExteriorStudLm,
		extraWallStudLm,
		extraCuts,
		extraMaterials,
		lateralSupportCount: walls.filter((wall) => wall.requiresLateralSupport)
			.length,
	};
}

export function createTwoRoomBuildContext(
	W: number,
	L: number,
	roofConfig: RoofConfig = DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum,
	roofJoists?: RoofJoistResult,
	wallHeightM?: number,
) {
	return createSquareBaseBuildContext(W, L, {
		roofConfig,
		roofJoistDepthM: getRoofJoistDepthM(roofJoists),
		wallHeightM,
	});
}

/**
 * The divider's HIGHEST top: the ridge on a gable (x = 0), the high-wall end
 * on a mono falling across the width, the one level height otherwise.
 */
function getDividerTopY(ctx: SquareBaseBuildContext, divZ: number): number {
	return (
		ctx.wallBase +
		Math.max(
			ctx.roofGeometry.getRoofHeightAt(0, divZ),
			ctx.roofGeometry.getRoofHeightAt(-ctx.W / 2, divZ),
			ctx.roofGeometry.getRoofHeightAt(ctx.W / 2, divZ),
		)
	);
}

function getDividerRoofYAtX(
	ctx: SquareBaseBuildContext,
	x: number,
	divZ: number,
): number {
	return ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(x, divZ);
}

function addDividerPanelMesh(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	points: Array<[number, number]>,
	zCenter: number,
	name: string,
) {
	if (points.length < 3) return;

	const shape = new THREE.Shape();
	const [firstX, firstY] = points[0];
	shape.moveTo(firstX, firstY);
	for (const [x, y] of points.slice(1)) {
		shape.lineTo(x, y);
	}
	shape.closePath();

	const geometry = new THREE.ExtrudeGeometry(shape, {
		depth: ctx.pT,
		bevelEnabled: false,
	});
	geometry.translate(0, 0, -ctx.pT / 2);
	const mesh = new THREE.Mesh(geometry, matDivider);
	mesh.position.z = zCenter;
	mesh.castShadow = true;
	mesh.receiveShadow = true;
	mesh.name = name;
	interiorGroup.add(mesh);
}

function buildDividerFraming(
	ctx: SquareBaseBuildContext,
	framingGroup: THREE.Group,
	divZ: number,
	config: TwoRoomConfig,
	openings: WallOpening[],
) {
	framingGroup = createDividerVisibilityGroup(framingGroup);
	const dividerDoor = resolveDividerDoor(ctx, openings);
	// Studs (and their 45mm bodies) stay clear of the door span plus the
	// foder legs beside it (~50mm past each door edge).

	// Where the partition meets the panorama (right) wall, drop a full-height
	// mullion stud so the divider lands on a real frame member and the glass
	// splits cleanly on either side of it (see getGlazingZonesForWall).
	if (config.glazingStyle === "panorama") {
		const dividerLocalXM = divZ + ctx.L / 2;
		const studTopM = wallHeightAtAxis(ctx, "right", dividerLocalXM) - ctx.sW;
		const studHeightM = studTopM - ctx.sW;
		if (studHeightM > 0.001) {
			addProjectedWallBox(
				ctx,
				"right",
				{
					axisCenterM: dividerLocalXM,
					axisSizeM: ctx.sW,
					yCenterM: ctx.wallBase + ctx.sW + studHeightM / 2,
					ySizeM: studHeightM,
					normalDepthM: ctx.sD,
				},
				matWood,
				framingGroup,
				"detail/framing/panorama-mullion",
			);
		}
	}

	const plan = createPartitionFramePlan(ctx.W, dividerDoor,
    x => getDividerRoofYAtX(ctx, x, divZ) - ctx.wallBase,
    ctx.roofConfig.kind === 'gable');
  for (const member of plan.members) {
    const mesh = member.slope
      ? addSlopedBox(member.width, member.height, ctx.sD, matWood, member.x, ctx.wallBase + member.y, divZ, framingGroup, member.slope, 'x')
      : addBox(member.width, member.height, ctx.sD, matWood, member.x, ctx.wallBase + member.y, divZ, framingGroup);
    mesh.name = `detail/framing/divider-${member.id}`;
  }
}

function resolveDividerDoor(
	ctx: SquareBaseBuildContext,
	openings: WallOpening[],
): WallOpening {
	const opening = openings.find(
		(candidate) => candidate.wall === "divider" && candidate.type === "door",
	);
	if (opening) {
		return opening;
	}

	const widthMm = Math.round(ctx.W * 1000);
	return {
		id: "default-divider-door",
		wall: "divider",
		type: "door",
		xMm: getCenteredDividerDoorX(widthMm),
		yMm: 0,
		widthMm: DEFAULT_DIVIDER_DOOR_WIDTH_MM,
		heightMm: DEFAULT_DIVIDER_DOOR_HEIGHT_MM,
		glazed: true,
	};
}

/**
 * Liggande glespanel on both faces of the delningsvägg.
 *
 * The divider is panelled with STANDING boards on each side, so — exactly like
 * every other wall — those boards need lying battens to be nailed into. Rows
 * run at the shared interior c/c up to the divider's ridge height, break around
 * the divider door, and are clipped per-sample to the roof profile at the
 * divider's Z so a gable row splits into two flank segments at the peak.
 *
 * No bastufolie or insulation here: the BOM lists the vapour barrier for the
 * sauna's three exterior walls + ceiling only, and this is an interior wall.
 */
function buildDividerBattens(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	divZ: number,
	bounds: { minX: number; maxX: number },
	door: { leftX: number; rightX: number; height: number },
	panelDirection: InteriorPanelDirection = "standing",
) {
	const battenD = ENGINE_INTERIOR_BATTEN_D_M;
	const battenW = ENGINE_INTERIOR_BATTEN_W_M;
	const halfStrip = battenW / 2;
	// Inner face flush with the panel's back face on each side.
	const faceOffset = ctx.sD / 2 + ctx.iL - battenD / 2;
	const maxHeight =
		getDividerTopY(ctx, divZ) - ctx.wallBase - ENGINE_INTERIOR_BATTEN_W_M;
	if (maxHeight <= 0) return;

	// Korsläkt on the delningsvägg too: a lying panel needs standing battens
	// here exactly as it does on the perimeter walls, and the BOM bills the
	// divider faces under the same rule.
	if (panelDirection === "lying") {
		buildDividerStandingBattens(ctx, interiorGroup, divZ, bounds, door, {
			battenD,
			battenW,
			faceOffset,
			maxHeight,
		});
		return;
	}

	const rowCenters: number[] = [halfStrip];
	for (
		let y = ENGINE_INTERIOR_BATTEN_CC_M;
		y < maxHeight - halfStrip;
		y += ENGINE_INTERIOR_BATTEN_CC_M
	) {
		rowCenters.push(y);
	}
	const topRow = maxHeight - halfStrip;
	if (topRow - rowCenters[rowCenters.length - 1] > 0.05)
		rowCenters.push(topRow);

	const SAMPLES = 32;
	for (const rowCenter of rowCenters) {
		const spans: Array<{ start: number; end: number }> =
			rowCenter - halfStrip < door.height && door.rightX > door.leftX
				? [
						{ start: bounds.minX, end: door.leftX },
						{ start: door.rightX, end: bounds.maxX },
					]
				: [{ start: bounds.minX, end: bounds.maxX }];

		for (const span of spans) {
			const width = span.end - span.start;
			if (width <= 0.02) continue;
			// Keep only the sub-runs where the divider still reaches this row.
			const rowTop = ctx.wallBase + rowCenter + halfStrip;
			let runStart: number | null = null;
			let prevInside = false;
			const kept: Array<{ start: number; end: number }> = [];
			for (let i = 0; i <= SAMPLES; i += 1) {
				const x = span.start + (width * i) / SAMPLES;
				const inside =
					ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(x, divZ) >= rowTop;
				if (inside && !prevInside) runStart = x;
				else if (!inside && prevInside && runStart !== null) {
					kept.push({ start: runStart, end: x });
					runStart = null;
				}
				prevInside = inside;
			}
			if (prevInside && runStart !== null) {
				kept.push({ start: runStart, end: span.end });
			}

			for (const run of kept) {
				const runWidth = run.end - run.start;
				if (runWidth <= 0.02) continue;
				for (const side of [-1, 1] as const) {
					addBox(
						runWidth,
						battenW,
						battenD,
						matWood,
						run.start + runWidth / 2,
						ctx.wallBase + rowCenter,
						divZ + side * faceOffset,
						interiorGroup,
					).name =
						`detail/interior-batten/two-room-divider/${side === -1 ? "sauna" : "relax"}-${rowCenter.toFixed(2)}-${run.start.toFixed(2)}`;
				}
			}
		}
	}
}

/**
 * The standing half of `buildDividerBattens` — one run per stud axis across
 * the divider, clipped to the roof profile and broken around the door, mirror
 * of the lying row generator above.
 */
function buildDividerStandingBattens(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	divZ: number,
	bounds: { minX: number; maxX: number },
	door: { leftX: number; rightX: number; height: number },
	dims: {
		battenD: number;
		battenW: number;
		faceOffset: number;
		maxHeight: number;
	},
) {
	const { battenD, battenW, faceOffset } = dims;
	const halfStrip = battenW / 2;
	const span = bounds.maxX - bounds.minX;
	if (span <= 0.02) return;

	// Same c/c grid the wall studs use, plus a run at each end — the axes the
	// BOM counts for a standing divider face.
	const columns: number[] = [];
	for (let x = bounds.minX; x < bounds.maxX; x += CC) columns.push(x);
	columns.push(bounds.maxX);

	for (const rawX of columns) {
		const x = Math.min(Math.max(rawX, bounds.minX + halfStrip), bounds.maxX - halfStrip);
		// The divider follows the roof, so each run stops where the wall does.
		const top =
			ctx.roofGeometry.getRoofHeightAt(x, divZ) - ENGINE_INTERIOR_BATTEN_W_M;
		if (top <= 0) continue;

		// A run crossing the door opening is split above it; below the head
		// there is no divider to batten.
		const crossesDoor =
			door.rightX > door.leftX && x > door.leftX && x < door.rightX;
		const segments = crossesDoor
			? [{ start: door.height, end: top }]
			: [{ start: 0, end: top }];

		for (const segment of segments) {
			const height = segment.end - segment.start;
			if (height <= 0.02) continue;
			for (const side of [-1, 1] as const) {
				addBox(
					battenW,
					height,
					battenD,
					matWood,
					x,
					ctx.wallBase + segment.start + height / 2,
					divZ + side * faceOffset,
					interiorGroup,
				).name =
					`detail/interior-batten/two-room-divider/${side === -1 ? "sauna" : "relax"}-${x.toFixed(2)}-${segment.start.toFixed(2)}`;
			}
		}
	}
}

function buildDividerInteriorFromOpenings(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	divZ: number,
	openings: WallOpening[],
	panelDirection: InteriorPanelDirection = DEFAULT_FINISHES.panelDirection,
) {
	interiorGroup = createDividerVisibilityGroup(interiorGroup);
	const dividerTopY = getDividerTopY(ctx, divZ);
	const dividerWallHeight = dividerTopY - ctx.wallBase;
	// The divider spans X at one Z: its top is level under a mono that falls
	// along Z, but follows the roof across X under a gable or a mono falling
	// across the width (high wall left/right) — then it needs profile panels.
	const dividerFollowsRoofProfile =
		ctx.roofConfig.kind === "gable" ||
		ctx.roofGeometry.monoSlope?.axis === "x";
	// Push panel face just proud of stud face to prevent z-fighting
	// (panel back face sat coplanar with stud surface = flicker).
	const dividerFaceOffset = ctx.sD / 2 + ctx.iL + ctx.pT / 2 + 0.001;
	const divInset = ctx.sD;
	const dividerMinX = -ctx.W / 2 + divInset;
	const dividerMaxX = ctx.W / 2 - divInset;
	const dividerDoor = resolveDividerDoor(ctx, openings);
	const doorWidth = dividerDoor.widthMm / 1000;
	const doorHeight = dividerDoor.heightMm / 1000;
	const doorCenterX = -ctx.W / 2 + dividerDoor.xMm / 1000 + doorWidth / 2;
	const doorCenterY =
		ctx.wallBase + dividerDoor.yMm / 1000 + doorHeight / 2 + 0.02;
	const doorLeftAbs = -ctx.W / 2 + dividerDoor.xMm / 1000;
	const doorRightAbs = doorLeftAbs + doorWidth;
	const topHeight = Math.max(0.12, dividerWallHeight - doorHeight);
	const topCenterY = ctx.wallBase + doorHeight + topHeight / 2;
	const leftWidth = Math.max(0, doorLeftAbs - dividerMinX);
	const rightWidth = Math.max(0, dividerMaxX - doorRightAbs);

	const addDividerPanelSegment = (
		width: number,
		height: number,
		centerX: number,
		centerY: number,
		section: "left" | "right" | "top",
	) => {
		if (width <= 0.04 || height <= 0.04) return;

		const saunaPanel = addBox(
			width,
			height,
			ctx.pT,
			matDivider,
			centerX,
			centerY,
			divZ - dividerFaceOffset,
			interiorGroup,
		);
		saunaPanel.name = `detail/divider-panel/sauna-${section}`;

		const relaxPanel = addBox(
			width,
			height,
			ctx.pT,
			matDivider,
			centerX,
			centerY,
			divZ + dividerFaceOffset,
			interiorGroup,
		);
		relaxPanel.name = `detail/divider-panel/relax-${section}`;
	};

	const addGableDividerPanelSegment = (
		x0: number,
		x1: number,
		bottomY: number,
		section: "left" | "right" | "top",
	) => {
		if (x1 - x0 <= 0.04) return;

		const topLeftY = ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(x0, divZ);
		const topRightY = ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(x1, divZ);
		if (Math.max(topLeftY, topRightY) - bottomY <= 0.04) return;

		const points: Array<[number, number]> = [
			[x0, bottomY],
			[x1, bottomY],
			[x1, topRightY],
		];
		if (x0 < 0 && x1 > 0) {
			points.push([
				0,
				ctx.wallBase + ctx.roofGeometry.getRoofHeightAt(0, divZ),
			]);
		}
		points.push([x0, topLeftY]);
		addDividerPanelMesh(
			ctx,
			interiorGroup,
			points,
			divZ - dividerFaceOffset,
			`detail/divider-panel/sauna-${section}`,
		);
		addDividerPanelMesh(
			ctx,
			interiorGroup,
			points,
			divZ + dividerFaceOffset,
			`detail/divider-panel/relax-${section}`,
		);
	};

	if (dividerFollowsRoofProfile) {
		addGableDividerPanelSegment(dividerMinX, doorLeftAbs, ctx.wallBase, "left");
		addGableDividerPanelSegment(
			doorRightAbs,
			dividerMaxX,
			ctx.wallBase,
			"right",
		);
		addGableDividerPanelSegment(
			doorLeftAbs - 0.02,
			doorRightAbs + 0.02,
			ctx.wallBase + doorHeight,
			"top",
		);
	} else if (leftWidth > 0.04) {
		addDividerPanelSegment(
			leftWidth,
			dividerWallHeight,
			dividerMinX + leftWidth / 2,
			ctx.wallBase + dividerWallHeight / 2,
			"left",
		);
	}

	if (!dividerFollowsRoofProfile && rightWidth > 0.04) {
		addDividerPanelSegment(
			rightWidth,
			dividerWallHeight,
			doorRightAbs + rightWidth / 2,
			ctx.wallBase + dividerWallHeight / 2,
			"right",
		);
	}

	if (!dividerFollowsRoofProfile) {
		addDividerPanelSegment(
			doorWidth + 0.04,
			topHeight,
			doorCenterX,
			topCenterY,
			"top",
		);
	}

	buildDividerBattens(
		ctx,
		interiorGroup,
		divZ,
		{ minX: dividerMinX, maxX: dividerMaxX },
		{ leftX: doorLeftAbs, rightX: doorRightAbs, height: doorHeight },
		panelDirection,
	);

	const doorMesh = addBox(
		doorWidth,
		doorHeight,
		ctx.pT,
		matGlass,
		doorCenterX,
		doorCenterY,
		divZ - ctx.pT,
		interiorGroup,
	);
	doorMesh.name = `detail/divider-door/${dividerDoor.id}`;
	if (dividerDoor.id === "default-divider-door") {
		doorMesh.name = "detail/divider-door";
	}

	addOpeningTrimFrame({
		group: interiorGroup,
		width: doorWidth,
		height: doorHeight,
		depth: 0.05,
		trimWidth: 0.045,
		// The divider door sits on the floor — a bottom board (and legs
		// running a trim-width past the door bottom) would bury itself in
		// the floor panel and the divider sill.
		includeBottom: false,
		material: matCornerTrim,
		horizontalMaterial: matCornerTrimHorizontal,
		position: new THREE.Vector3(doorCenterX, doorCenterY, divZ - ctx.pT - 0.01),
		orientation: "z",
		namePrefix: `divider-door-${dividerDoor.id}`,
	});
}

function addTwoRoomDoorFallbacks(
	ctx: SquareBaseBuildContext,
	openings: WallOpening[],
	framingGroup: THREE.Group,
	exteriorGroup: THREE.Group,
	frontFramingGroup: THREE.Group,
	frontExteriorGroup: THREE.Group,
) {
	for (const opening of openings) {
		if (opening.type !== "door") continue;
		if (
			opening.wall !== "front" &&
			opening.wall !== "right" &&
			opening.wall !== "left"
		)
			continue;

		const width = opening.widthMm / 1000;
		const height = opening.heightMm / 1000;
		const y = ctx.wallBase + opening.yMm / 1000 + height / 2;
		const isFront = opening.wall === "front";
		// Long-wall entry sits on ±X; left wall mirrors the right.
		const longSign = opening.wall === "left" ? -1 : 1;
		const x = isFront
			? -ctx.W / 2 + opening.xMm / 1000 + width / 2
			: longSign * (ctx.W / 2 - ctx.sD - ctx.iL - ctx.pT / 2 + 0.012);
		const z = isFront
			? ctx.L / 2 - ctx.sD - ctx.iL - ctx.pT / 2 + 0.012
			: -ctx.L / 2 + opening.xMm / 1000 + width / 2;
		const targetFramingGroup = isFront ? frontFramingGroup : framingGroup;
		const targetExteriorGroup = isFront ? frontExteriorGroup : exteriorGroup;

		// The karm frame is buried in the wall assembly around the rough
		// opening. Sized exactly to the opening, its reveal faces landed ON
		// the opening planes — the head board's underside shared the header
		// framing's underside (and the panel-above-door's bottom edge), so
		// looking up through the slot above the door leaf z-fought/flickered.
		// Shrink the frame 2mm per side (reveal faces sit just inside the
		// opening, covering the neighbouring end faces) and pull it 4mm
		// toward the room so no face is coplanar with panel or header.
		const KARM_REVEAL_INSET_M = 0.004;
		addOpeningTrimFrame({
			group: targetFramingGroup,
			width: width - KARM_REVEAL_INSET_M,
			height: height - KARM_REVEAL_INSET_M,
			depth: 0.06,
			trimWidth: 0.045,
			// Entry doors sit on the floor — a bottom karm board would bury
			// itself in the floor panel and the joists at the threshold.
			includeBottom: false,
			material: matCornerTrim,
			horizontalMaterial: matCornerTrimHorizontal,
			position: new THREE.Vector3(
				isFront ? x : x - longSign * KARM_REVEAL_INSET_M,
				y,
				isFront
					? ctx.L / 2 -
							ctx.sD -
							ctx.iL -
							ctx.pT / 2 +
							0.02 -
							KARM_REVEAL_INSET_M
					: z,
			),
			orientation: isFront ? "z" : "x",
			namePrefix: `two-room-${opening.wall}-door-${opening.id}`,
		});

		const doorRoot = new THREE.Group();
		doorRoot.name = `detail/procedural-door/${opening.id}`;
		doorRoot.position.set(x, y, z);
		if (!isFront) {
			doorRoot.rotation.y = longSign * (Math.PI / 2);
		}

		// The entry leaf owns its infill; wall panels only own window glass.
		const leafW = Math.max(0.6, width - 0.08);
		const leafH = Math.max(1.5, height - 0.08);
		const leafD = 0.045;
		const frameW = Math.min(0.1, leafW * 0.16);
		const panelD = leafD * 0.55;
		const innerW = leafW - 2 * frameW;
		const innerH = leafH - 2 * frameW;

		const doorParts = [
			{
				part: "stile-l",
				w: frameW,
				h: leafH,
				d: leafD,
				x: -leafW / 2 + frameW / 2,
				y: 0,
				mat: matDoorWood,
			},
			{
				part: "stile-r",
				w: frameW,
				h: leafH,
				d: leafD,
				x: leafW / 2 - frameW / 2,
				y: 0,
				mat: matDoorWood,
			},
			{
				part: "rail-top",
				w: innerW,
				h: frameW,
				d: leafD,
				x: 0,
				y: leafH / 2 - frameW / 2,
				mat: matDoorWood,
			},
			{
				part: "rail-bottom",
				w: innerW,
				h: frameW,
				d: leafD,
				x: 0,
				y: -leafH / 2 + frameW / 2,
				mat: matDoorWood,
			},
			{
				part: "leaf",
				w: innerW - 0.012,
				h: innerH - 0.012,
				d: panelD,
				x: 0,
				y: 0,
				mat: opening.glazed === true ? matGlass : matDoorWood,
			},
		];

		for (const piece of doorParts) {
			const mesh = new THREE.Mesh(
				new THREE.BoxGeometry(piece.w, piece.h, piece.d),
				piece.mat,
			);
			mesh.position.set(piece.x, piece.y, 0);
			mesh.castShadow = piece.mat !== matGlass;
			mesh.receiveShadow = true;
			mesh.name = `detail/procedural-door-${piece.part}/${opening.id}`;
			doorRoot.add(mesh);
		}

		const handle = new THREE.Mesh(
			new THREE.BoxGeometry(0.028, 0.3, 0.028),
			matDoorHandle,
		);
		handle.position.set(
			(opening.hingeSide === "right" ? -1 : 1) * (leafW / 2 - frameW - 0.045),
			-0.1,
			leafD / 2 + 0.03,
		);
		handle.name = `detail/procedural-door-handle/${opening.id}`;
		doorRoot.add(handle);

		targetExteriorGroup.add(doorRoot);
	}
}

function resolveTwoRoomCoreOptions(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	frontInteriorGroup: THREE.Group,
	framingResults: Map<"front" | "back" | "left" | "right", WallFramingResult>,
	roomLayout: TwoRoomRoomLayout,
	openings: WallOpening[],
	config: TwoRoomConfig,
	heater: HeaterRef = "wood",
	heaterPlacementZMm?: number,
	interior?: InteriorRenderPlan,
	panelDirection: InteriorPanelDirection = DEFAULT_FINISHES.panelDirection,
): Omit<Parameters<typeof buildSaunaRoomCore>[3], "chimneyStack"> {
	const heaterType = heaterKindOf(heater);

	const innerW = ctx.W - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	const heaterW = getHeaterWidth(heater);
	const heaterD = getHeaterDepth(heater);
	const placement = calculateHeaterPlacement({
		saunaType: "tvarum",
		widthM: ctx.W,
		lengthM: ctx.L,
		heaterType,
		heaterModelId: heaterModelIdOf(heater),
		roofKind: ctx.roofConfig.kind,
		partitionSplit: roomLayout.hasRelaxRoom
			? roomLayout.saunaLength / ctx.L
			: undefined,
		preferredCenterZMm: heaterPlacementZMm,
	});
	const dividerFaceOffset = ctx.sD / 2 + ctx.iL + ctx.pT / 2 + 0.001;
	const shieldThickness = 0.02;
	// Far end of the sauna room from the divider — also where the exhaust sits.
	const saunaEndZ =
		roomLayout.saunaDirection === 1
			? roomLayout.saunaMinZ
			: roomLayout.saunaMaxZ;
	const dividerShieldCenterZ =
		roomLayout.dividerZ -
		roomLayout.saunaDirection * (dividerFaceOffset + shieldThickness / 2);
	// Behind the divider shield, slid off it where that puts the flue in a
	// roof-joist bay — one owner with the BOM's clearance layout.
	const wallMountedHeaterCenterZ = dividerHeaterCenterZM({
		lengthM: ctx.L,
		dividerZM: roomLayout.dividerZ,
		saunaDirection: roomLayout.saunaDirection,
		saunaEndZM: saunaEndZ,
		heater,
	});
	const oneRoomHeaterCenterZ = placement.selectedCenterZMm / 1000;
	// Heater (and its shields + intake) stay off the panorama glass: mount them
	// on the long wall opposite the panorama. The long-wall entry door moves to
	// the same opposite wall, but with a relax room the divider keeps the heater
	// (sauna side) clear of the entry (relax side).
	const heaterSign = getTwoRoomHeaterWall(config) === "right" ? 1 : -1;
	// The view layout backs the unit onto the front end wall, out by the glass.
	const viewHeater = roomLayout.viewLayout
		? getViewLayoutHeaterCenter(
				innerW,
				roomLayout.saunaLength,
				heaterW,
				heaterD,
				0.15,
			)
		: null;
	const heaterCenterX =
		viewHeater?.x ?? heaterSign * (innerW / 2 - heaterW / 2 - 0.15);
	const heaterWallX = heaterSign * (innerW / 2);
	// Exhaust grille goes on the left long wall — solid in both glazing modes
	// (the panorama glass is on the right), so it never lands on glass.
	const exhaustWallX = -(innerW / 2);
	let heaterCenterZ = viewHeater
		? roomLayout.saunaCenterZ + viewHeater.z
		: roomLayout.hasRelaxRoom && heaterType === "wood"
			? wallMountedHeaterCenterZ
			: oneRoomHeaterCenterZ;

	// One-room layouts have no divider to separate the heater from a long-wall
	// entry door on the same wall — nudge the heater clear so it never blocks
	// the doorway. The view layout's unit is on another wall altogether.
	if (!roomLayout.hasRelaxRoom && !viewHeater) {
		const heaterWallId = getTwoRoomHeaterWall(config);
		const doorOnHeaterWall = openings.find(
			(opening) => opening.type === "door" && opening.wall === heaterWallId,
		);
		if (doorOnHeaterWall) {
			const doorMinZ = -ctx.L / 2 + doorOnHeaterWall.xMm / 1000;
			const doorMaxZ = doorMinZ + doorOnHeaterWall.widthMm / 1000;
			const clearance = 0.1;
			const overlaps =
				heaterCenterZ - heaterD / 2 < doorMaxZ + clearance &&
				heaterCenterZ + heaterD / 2 > doorMinZ - clearance;
			if (overlaps) {
				const frontZ = doorMaxZ + clearance + heaterD / 2;
				const backZ = doorMinZ - clearance - heaterD / 2;
				const frontFits = frontZ + heaterD / 2 <= roomLayout.saunaMaxZ - 0.1;
				const backFits = backZ - heaterD / 2 >= roomLayout.saunaMinZ + 0.1;
				if (frontFits) {
					heaterCenterZ = frontZ;
				} else if (backFits) {
					heaterCenterZ = backZ;
				}
			}
		}
	}

	// Both rooms' lavar — the hot room's AND the relax bench — now come out of
	// one `InteriorLayout`. The relax bench used to be drawn separately below
	// this call, from `getRelaxBenchPlacement` directly; that placement is still
	// the owner, but it reaches the model as a `BenchRun` so the thing drawn and
	// the thing billed are literally the same object.
	const rooms = getTwoRoomInteriorRooms(ctx, roomLayout);
	const heaterSize = getHeaterSizeMm(heater);
	const generatedHeater = twoRoomHeaterPlacement(
		rooms[0],
		heaterCenterX,
		heaterCenterZ,
		heaterSize,
		viewHeater ? "front" : undefined,
	);
	const benchPlacement = getHotRoomBenchPlacement(roomLayout, {
		openings,
		lengthM: ctx.L,
	});
	const plan =
		interior ??
		createInteriorRenderPlan({
			layout: reconcileInteriorLayout(
				createInteriorLayout(rooms[0], {
					variant: benchPlacement.variant,
					heater: generatedHeater,
					benchAnchor: benchPlacement,
					extraRuns: relaxBenchRun(roomLayout, config),
				}),
				rooms,
				heaterSize,
			),
			rooms,
			heaterSize,
			generatedHeater,
			namePrefix: "two-room",
		});

	const drawnHeaterX = plan.heater.centerXM;
	const drawnHeaterZ = plan.heater.centerZM;
	// The strålskydd are built against the wall the ENGINE picked. If the user
	// has moved the unit off that placement, the shields follow it rather than
	// stay behind protecting nothing — which is exactly the case where
	// `deriveHeaterClearanceLayout` stops granting the shield exemptions.
	const shieldWall = plan.heater.wall;
	const shieldOnLongWall = shieldWall === "left" || shieldWall === "right";
	const sideShieldPosition =
		plan.heater.atGeneratedPlacement && shieldOnLongWall
		? new THREE.Vector3(heaterWallX, ctx.wallBase + 0.55, drawnHeaterZ)
		: shieldOnLongWall
			? new THREE.Vector3(
					(shieldWall === "left" ? rooms[0].minXMm : rooms[0].maxXMm) / 1000,
					ctx.wallBase + 0.55,
					drawnHeaterZ,
				)
			: new THREE.Vector3(
					drawnHeaterX,
					ctx.wallBase + 0.55,
					(shieldWall === "back" ? rooms[0].minZMm : rooms[0].maxZMm) / 1000,
				);
	// The supply valve follows the heater only while the heater is on a long
	// wall; otherwise it stays where the engine put it.
	const intakeWallX = shieldOnLongWall
		? (shieldWall === "left" ? rooms[0].minXMm : rooms[0].maxXMm) / 1000
		: heaterWallX;
	// A short-wall placement opens away from its own wall; a long-wall one keeps
	// the engine's choice (away from the divider for a +Z sauna).
	const heaterRotationY =
		heaterDoorRotationY(shieldWall) ??
		(roomLayout.hasRelaxRoom && roomLayout.saunaPosition === "right"
			? 0
			: Math.PI);
	// The divider shield only stands where the divider is; once the unit has left
	// it, the plate goes back to being the freestanding one in front of the door.
	const frontShieldZ =
		roomLayout.hasRelaxRoom &&
		heaterType === "wood" &&
		plan.heater.atGeneratedPlacement
			? dividerShieldCenterZ
			: drawnHeaterZ + (shieldWall === "front" ? -1 : 1) * (heaterW / 2 + 0.12);
	// The view layout's unit backs onto the front end wall with its door facing
	// the room; the freestanding plate stands beside it on the lavar's side
	// instead of in front of the fire door, and the supply valve sits low on
	// the wall behind it — the entry wall's end is taken by the door. That
	// holds wherever the user slides the unit along that wall, so it keys on
	// the wall the drawn heater stands against, not on the generated spot.
	const viewHeaterOnFrontWall =
		roomLayout.viewLayout && plan.heater.wall === "front";
	const frontShieldPosition = viewHeaterOnFrontWall
		? new THREE.Vector3(
				drawnHeaterX - (heaterW / 2 + 0.12),
				ctx.wallBase + 0.55,
				drawnHeaterZ,
			)
		: new THREE.Vector3(drawnHeaterX, ctx.wallBase + 0.55, frontShieldZ);

	const coreOptions: Omit<
		Parameters<typeof buildSaunaRoomCore>[3],
		"chimneyStack"
	> = {
		namePrefix: "two-room",
		panelDirection,
		framingResults,
		heaterType: heater,
		// Bastufolie belongs in the hot room only — which is also exactly what
		// the BOM buys (sauna end wall + both long walls over the band, plus the
		// unclipped ceiling; see `bastufolieWallArea`). The band runs from the
		// building envelope to the divider, NOT from saunaMinZ/saunaMaxZ: those
		// are the finished-room bounds, which sit inside the wall planes the
		// foil is applied to. Without a relax room the whole building is sauna.
		vaporBarrierZRange: roomLayout.hasRelaxRoom
			? roomLayout.saunaCenterZ < roomLayout.dividerZ
				? { min: -ctx.L / 2 - 0.05, max: roomLayout.dividerZ }
				: { min: roomLayout.dividerZ, max: ctx.L / 2 + 0.05 }
			: undefined,
		benches: plan.benches,
		heater: new THREE.Vector3(
			drawnHeaterX,
			// Rest the heater on the floor regardless of type — the electric
			// tower is 0.9m tall, so a fixed wood-height offset sank it ~90mm
			// into the floor joists.
			ctx.wallBase + getHeaterHeight(heater) / 2 + 0.005,
			drawnHeaterZ,
		),
		heaterRotationY,
		frontShield: {
			group: roomLayout.hasRelaxRoom ? frontInteriorGroup : interiorGroup,
			position: frontShieldPosition,
			normal: viewHeaterOnFrontWall ? "x" : "z",
		},
		sideShield: sideShieldPosition,
		sideShieldWall: shieldWall,
		intakeVent: {
			group: interiorGroup,
			// Fresh-air intake sits on the exterior long wall behind the heater;
			// it is mounted on that wall's inner face, so it points into the room.
			// It stays on a LONG wall even when an authored heater has moved to a
			// short one — the valve's own geometry faces ±x, and the short walls
			// are the gable/divider ends, which are not where supply air comes in.
			position: viewHeaterOnFrontWall
				? new THREE.Vector3(
						drawnHeaterX,
						ctx.wallBase + 0.15,
						rooms[0].maxZMm / 1000,
					)
				: new THREE.Vector3(intakeWallX, ctx.wallBase + 0.15, drawnHeaterZ),
			facing: viewHeaterOnFrontWall ? "-z" : intakeWallX > 0 ? "-x" : "+x",
		},
		exhaustVent: {
			group: interiorGroup,
			// Exhaust is on the left long wall (`exhaustWallX = -innerW/2`), so
			// its inner face looks toward +x.
			position: new THREE.Vector3(
				exhaustWallX,
				ctx.wallBase + ctx.H - 0.2,
				saunaEndZ + roomLayout.saunaDirection * 0.2,
			),
			facing: "+x",
		},
	};
	for (const vent of [coreOptions.intakeVent, coreOptions.exhaustVent]) {
		if (vent) fitVentBetweenStuds(ctx, framingResults, vent);
	}
	return coreOptions;
}

/**
 * Where the drawn chimney rises, for the roof's skorstensväxling: over a wood
 * stove, nowhere otherwise. With the registry's resolved plan that is its
 * heater — the very centre the interior stands the stove on. A direct call
 * without one resolves the core options (which only reads the framing) to get
 * the generated placement, so the roof and the interior still agree.
 */
function twoRoomFlueAxis(
	ctx: SquareBaseBuildContext,
	framingResults: () => Map<"front" | "back" | "left" | "right", WallFramingResult>,
	scenePlan: Pick<TwoRoomScenePlan, "roomLayout" | "sceneOpenings" | "config">,
	heater: HeaterRef,
	heaterPlacementZMm: number | undefined,
	interior: InteriorRenderPlan | undefined,
): FlueAxisM | null {
	if (heaterKindOf(heater) !== "wood") return null;
	if (interior) {
		return { x: interior.heater.centerXM, z: interior.heater.centerZM };
	}
	const { heater: center } = resolveTwoRoomCoreOptions(
		ctx,
		new THREE.Group(),
		new THREE.Group(),
		framingResults(),
		scenePlan.roomLayout,
		scenePlan.sceneOpenings,
		scenePlan.config,
		heater,
		heaterPlacementZMm,
	);
	return { x: center.x, z: center.z };
}

function buildTwoRoomInterior(
	ctx: SquareBaseBuildContext,
	interiorGroup: THREE.Group,
	frontInteriorGroup: THREE.Group,
	framingResults: Map<"front" | "back" | "left" | "right", WallFramingResult>,
	roomLayout: TwoRoomRoomLayout,
	openings: WallOpening[],
	config: TwoRoomConfig,
	heater: HeaterRef = "wood",
	heaterPlacementZMm: number | undefined,
	interior: InteriorRenderPlan | undefined,
	panelDirection: InteriorPanelDirection = DEFAULT_FINISHES.panelDirection,
	chimneyInput: ChimneyBuildInput,
) {
	const options = resolveTwoRoomCoreOptions(
		ctx,
		interiorGroup,
		frontInteriorGroup,
		framingResults,
		roomLayout,
		openings,
		config,
		heater,
		heaterPlacementZMm,
		interior,
		panelDirection,
	);
	buildInteriorCeilingLayers(
		ctx,
		interiorGroup,
		"two-room",
		heaterKindOf(heater) === "wood"
			? { x: options.heater.x, z: options.heater.z }
			: null,
	);
	buildSaunaRoomCore(ctx, interiorGroup, frontInteriorGroup, {
		...options,
		// Measured over the roof where this stove's flue passes it, as
		// `withChimney` does from the same layout heater.
		chimneyStack: chimneyStackFor(chimneyInput, {
			x: options.heater.x,
			z: options.heater.z,
		}),
	});
	if (roomLayout.hasRelaxRoom) {
		buildDividerInteriorFromOpenings(
			ctx,
			interiorGroup,
			roomLayout.dividerZ,
			openings,
			panelDirection,
		);
	}
}

/**
 * The two rooms an interior layout may place runs in, hot room first.
 *
 * Mirrors `deriveInteriorLayoutContext`: the hot room is narrowed to the sauna
 * side of the divider, the relax room takes the rest, and without a divider the
 * whole finished interior IS the hot room.
 */
export function getTwoRoomInteriorRooms(
	ctx: SquareBaseBuildContext,
	roomLayout: TwoRoomRoomLayout,
): InteriorRoom[] {
	const innerW = ctx.W - 2 * ctx.sD - 2 * ctx.iL - 2 * ctx.pT;
	const sauna: InteriorRoom = {
		id: "sauna",
		minXMm: (-innerW / 2) * 1000,
		maxXMm: (innerW / 2) * 1000,
		minZMm: roomLayout.saunaMinZ * 1000,
		maxZMm: roomLayout.saunaMaxZ * 1000,
	};
	if (!roomLayout.hasRelaxRoom) return [sauna];
	return [
		sauna,
		{
			id: "relax",
			minXMm: sauna.minXMm,
			maxXMm: sauna.maxXMm,
			minZMm: roomLayout.relaxMinZ * 1000,
			maxZMm: roomLayout.relaxMaxZ * 1000,
		},
	];
}

/** The engine's own heater position, expressed in layout coordinates. */
function twoRoomHeaterPlacement(
	room: InteriorRoom,
	centerXM: number,
	centerZM: number,
	size: HeaterSizeMm,
	shortWall?: "front",
): HeaterPlacement {
	if (shortWall) {
		// Backed onto the front end wall: the width runs along X there.
		return {
			wall: shortWall,
			offsetMm: centerXM * 1000 - room.minXMm,
			wallGapMm: Math.max(0, room.maxZMm - (centerZM * 1000 + size.depthMm / 2)),
		};
	}
	const wall: InteriorWall = centerXM >= 0 ? "right" : "left";
	const halfWidthMm = size.widthMm / 2;
	return {
		wall,
		offsetMm: centerZM * 1000 - room.minZMm,
		wallGapMm: Math.max(
			0,
			wall === "right"
				? room.maxXMm - (centerXM * 1000 + halfWidthMm)
				: centerXM * 1000 - halfWidthMm - room.minXMm,
		),
	};
}

/**
 * The relax bench as a `BenchRun`, so the generated layout carries the bench the
 * engine draws. `getRelaxBenchPlacement` stays the single owner of where it
 * goes; this only restates it in the layout's vocabulary — the same conversion
 * `interiorLayoutResolver` makes for the BOM.
 */
function relaxBenchRun(
	roomLayout: TwoRoomRoomLayout,
	config: TwoRoomConfig,
): BenchRun[] {
	const relax = getRelaxBenchPlacement(roomLayout, config);
	if (!relax) return [];
	return [
		{
			id: "bench-relax",
			room: "relax",
			wall: relax.wall,
			tier: "lower",
			startMm: relax.startMm,
			lengthMm: relax.lengthMm,
			depthMm: relax.depthMm,
			offsetFromWallMm: relax.offsetFromWallMm,
			legHeightMm: relax.legHeightMm,
		},
	];
}

function getTwoRoomVentPassages(
	ctx: SquareBaseBuildContext,
	input: BuildInput,
	interior?: InteriorRenderPlan,
): VentPassage[] {
	const scenePlan = resolveTwoRoomScenePlan(input, ctx);
	const options = resolveTwoRoomCoreOptions(
		ctx,
		new THREE.Group(),
		new THREE.Group(),
		buildTwoRoomWallFraming(ctx, scenePlan),
		scenePlan.roomLayout,
		scenePlan.sceneOpenings,
		scenePlan.config,
		input.thermalInputs,
		input.thermalInputs.heaterPlacementZMm,
		interior,
	);
	return (
		[
			["intake", "supply", options.intakeVent],
			["exhaust", "exhaust", options.exhaustVent],
		] as const
	).flatMap(([name, kind, vent]) =>
		vent
			? [
					{
						name: `two-room-${name}`,
						kind,
						position: vent.position,
						facing: vent.facing,
					},
				]
			: [],
	);
}

function buildTwoRoom3D(
	input: BuildInput,
	interior?: InteriorRenderPlan,
): BuildResult {
	const structuralRoofJoists = getStructuralRoofJoists(
		resolveAdvisoryRoofJoists(input),
	);
	const ctx = createTwoRoomBuildContext(
		input.dimensions.width,
		input.dimensions.length,
		input.roofConfig,
		structuralRoofJoists,
		input.wallHeightM,
	);
	const scenePlan = resolveTwoRoomScenePlan(input, ctx);
	const foundation = calculateFoundation(
		input.dimensions.width,
		input.dimensions.length,
		input.siteConditions,
	);
	const saunaGroup = new THREE.Group();
	const foundationGroup = new THREE.Group();
	const deckGroup = new THREE.Group();
	const framingGroup = new THREE.Group();
	const panelGroup = new THREE.Group();
	const roofGroup = new THREE.Group();
	const interiorGroup = new THREE.Group();
	const exteriorGroup = new THREE.Group();
	const frontVisibilityGroups: FrontVisibilityGroups = {
		framing: createFrontVisibilityGroup("framing", framingGroup),
		panel: createFrontVisibilityGroup("panel", panelGroup),
		roof: createFrontVisibilityGroup("roof", roofGroup),
		interior: createFrontVisibilityGroup("interior", interiorGroup),
		exterior: createFrontVisibilityGroup("exterior", exteriorGroup),
	};

	buildFoundation(ctx, foundation, foundationGroup, {
		namePrefix: "two-room",
		includeEntrySteps: true,
		entryPlacement: scenePlan.entryPlacement,
		siteConditions: input.siteConditions,
		deckConfig: input.deckConfig,
	});
	buildFloorJoists(ctx, framingGroup, panelGroup, { namePrefix: "two-room" });
	const framingResults = buildTwoRoomWallFraming(
		ctx,
		scenePlan,
		framingGroup,
		frontVisibilityGroups.framing!,
	);
	if (scenePlan.roomLayout.hasRelaxRoom) {
		buildDividerFraming(
			ctx,
			framingGroup,
			scenePlan.roomLayout.dividerZ,
			scenePlan.config,
			scenePlan.sceneOpenings,
		);
	}
	buildPanels(ctx, framingResults, panelGroup, frontVisibilityGroups.panel!, {
		namePrefix: "two-room",
		mode: "shell",
			renderDoorGlass: false,
		panelDirection: input.finishes?.panelDirection,
	});
	buildInteriorCeilingFinish(
		ctx,
		panelGroup,
		"two-room",
		frontVisibilityGroups.panel!,
	);
	buildExteriorFacade(
		ctx,
		framingResults,
		exteriorGroup,
		frontVisibilityGroups.exterior!,
		{
			namePrefix: "two-room",
			mode: "ventilated",
			baseTrimSuppressedWalls: getDeckSuppressedWalls(input.deckConfig),
		},
	);
	addTwoRoomDoorFallbacks(
		ctx,
		scenePlan.sceneOpenings,
		framingGroup,
		exteriorGroup,
		frontVisibilityGroups.framing!,
		frontVisibilityGroups.exterior!,
	);
	buildRoof(ctx, roofGroup, frontVisibilityGroups.roof!, {
		namePrefix: "two-room",
		style: "sloped-detailed",
		flue: twoRoomFlueAxis(
			ctx,
			() => framingResults,
			scenePlan,
			input.thermalInputs,
			input.thermalInputs.heaterPlacementZMm,
			interior,
		),
	});
	buildTwoRoomInterior(
		ctx,
		interiorGroup,
		frontVisibilityGroups.interior!,
		framingResults,
		scenePlan.roomLayout,
		scenePlan.sceneOpenings,
		scenePlan.config,
		input.thermalInputs,
		input.thermalInputs.heaterPlacementZMm,
		interior,
		input.finishes?.panelDirection,
		input,
	);

	saunaGroup.add(
		foundationGroup,
		framingGroup,
		panelGroup,
		roofGroup,
		interiorGroup,
		exteriorGroup,
	);
	if (deckGroup.children.length > 0) {
		saunaGroup.add(deckGroup);
	}

	const passages = getTwoRoomVentPassages(ctx, input, interior);
	cutVentPassages(saunaGroup, ctx, passages);
	for (const passage of passages) addVentPassageExterior(ctx, exteriorGroup, frontVisibilityGroups.exterior!, interiorGroup, passage);

	return {
		saunaGroup,
		foundationGroup,
		deckGroup,
		framingGroup,
		panelGroup,
		roofGroup,
		interiorGroup,
		exteriorGroup,
		frontVisibilityGroups,
		premiumPlacements: [],
	};
}

function buildTwoRoomSubsystemGeometry(
	subsystem: DirtyFlag,
	input: BuildInput,
	ctx: SquareBaseBuildContext,
	/** See `buildSquareSubsystem`: an interior rebuild without it loses the layout. */
	interior?: InteriorRenderPlan,
): THREE.Group {
	const group = new THREE.Group();
	const scenePlan = resolveTwoRoomScenePlan(input, ctx);

	switch (subsystem) {
		case DirtyFlag.Foundation:
			buildFoundation(
				ctx,
				calculateFoundation(
					input.dimensions.width,
					input.dimensions.length,
					input.siteConditions,
				),
				group,
				{
					namePrefix: "two-room",
					includeEntrySteps: true,
					entryPlacement: scenePlan.entryPlacement,
					siteConditions: input.siteConditions,
					deckConfig: input.deckConfig,
				},
			);
			return group;
		case DirtyFlag.Deck:
			return group;
		case DirtyFlag.Framing:
			buildFloorJoists(ctx, group, new THREE.Group(), {
				namePrefix: "two-room",
			});
			buildTwoRoomWallFraming(
				ctx,
				scenePlan,
				group,
				createFrontVisibilityGroup("framing", group),
			);
			if (scenePlan.roomLayout.hasRelaxRoom) {
				buildDividerFraming(
					ctx,
					group,
					scenePlan.roomLayout.dividerZ,
					scenePlan.config,
					scenePlan.sceneOpenings,
				);
			}
			return group;
		case DirtyFlag.Panel: {
			const framingResults = buildTwoRoomWallFraming(ctx, scenePlan);
			buildFloorJoists(ctx, new THREE.Group(), group, {
				namePrefix: "two-room",
			});
			const frontPanelGroup = createFrontVisibilityGroup("panel", group);
			buildPanels(ctx, framingResults, group, frontPanelGroup, {
				namePrefix: "two-room",
				mode: "shell",
			renderDoorGlass: false,
				panelDirection: input.finishes?.panelDirection,
			});
			buildInteriorCeilingFinish(ctx, group, "two-room", frontPanelGroup);
			return group;
		}
		case DirtyFlag.Roof:
			buildRoof(ctx, group, createFrontVisibilityGroup("roof", group), {
				namePrefix: "two-room",
				style: "sloped-detailed",
				flue: twoRoomFlueAxis(
					ctx,
					() => buildTwoRoomWallFraming(ctx, scenePlan),
					scenePlan,
					input.thermalInputs?.heaterType ? input.thermalInputs : "wood",
					input.thermalInputs?.heaterPlacementZMm,
					interior,
				),
			});
			return group;
		case DirtyFlag.Interior: {
			const framingResults = buildTwoRoomWallFraming(ctx, scenePlan);
			buildTwoRoomInterior(
				ctx,
				group,
				createFrontVisibilityGroup("interior", group),
				framingResults,
				scenePlan.roomLayout,
				scenePlan.sceneOpenings,
				scenePlan.config,
				input.thermalInputs?.heaterType ? input.thermalInputs : "wood",
				input.thermalInputs?.heaterPlacementZMm,
				interior,
				input.finishes?.panelDirection,
				input,
			);
			return group;
		}
		case DirtyFlag.Exterior: {
			const framingResults = buildTwoRoomWallFraming(ctx, scenePlan);
			const frontExteriorGroup = createFrontVisibilityGroup("exterior", group);
			buildExteriorFacade(ctx, framingResults, group, frontExteriorGroup, {
				namePrefix: "two-room",
				mode: "ventilated",
				baseTrimSuppressedWalls: getDeckSuppressedWalls(input.deckConfig),
			});
			addTwoRoomDoorFallbacks(
				ctx,
				scenePlan.sceneOpenings,
				new THREE.Group(),
				group,
				new THREE.Group(),
				frontExteriorGroup,
			);
			return group;
		}
		default:
			return group;
	}
}

export function buildTwoRoomSubsystem(
	subsystem: DirtyFlag,
	input: BuildInput,
	ctx: SquareBaseBuildContext,
	interior?: InteriorRenderPlan,
): THREE.Group {
	const group = buildTwoRoomSubsystemGeometry(subsystem, input, ctx, interior);
	if (
		subsystem === DirtyFlag.Panel ||
		subsystem === DirtyFlag.Interior ||
		subsystem === DirtyFlag.Exterior
	) {
		const passages = getTwoRoomVentPassages(ctx, input, interior);
		cutVentPassages(group, ctx, passages);
		for (const passage of passages) {
			if (subsystem === DirtyFlag.Exterior)
				addVentPassageExterior(
					ctx,
					group,
					getFrontVisibilityGroup(group, "exterior") ?? group,
					null,
					passage,
				);
			if (subsystem === DirtyFlag.Interior)
				addVentPassageExterior(ctx, null, null, group, passage);
		}
	}
	return group;
}

function calculateTwoRoomMaterials(
	W: number,
	L: number,
	roofConfig: RoofConfig = DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum,
	siteConditions: SiteConditions = DEFAULT_SITE_CONDITIONS,
	partitionSplit = 0.5,
	openings: WallOpening[] = [],
	heaterType: HeaterType = "wood",
	roofJoists: RoofJoistResult | undefined,
	config: TwoRoomConfig,
	/** See `RawSaunaEngine.calculateMaterials`. */
	bench?: BenchMaterialsResult,
	/** See the same parameter on the square engine — only `panelDirection` is read here. */
	finishesInput?: FinishConfig,
	/** `BuildInput.wallHeightM` (issue 155). */
	wallHeightM?: number,
): RawCalculationResult {
	const finishes = finishesInput ?? DEFAULT_FINISHES;
	const H = resolveWallHeightM(wallHeightM);
	const structuralRoofJoists = getStructuralRoofJoists(roofJoists);
	const roofGeometry = createRectRoofGeometry({
		width: W,
		length: L,
		wallHeight: H,
		roofConfig,
	});
	const pitchRad = roofGeometry.pitchRad;
	const roofRise = roofGeometry.roofRise;
	// Mono fall: along the length (front/back high wall, the historical shape)
	// or across the width (left/right high wall — the panorama presets). The
	// run is the dimension the roof rises over; the raked walls' hammarband is
	// that run along the slope, the two eave walls stay level.
	const monoAcross = roofGeometry.monoSlope?.axis === "x";
	const monoRunL = roofGeometry.monoSlope?.runLength ?? L;
	const monoEaveL = roofGeometry.monoSlope?.eaveLength ?? W;
	const monoSlopedL = monoRunL / Math.cos(Math.max(pitchRad, 0.0001));
	// addSlopedBox shears the horizontal run — the renderer adds its 0.2 m
	// overhang to the HORIZONTAL half-width before shearing, so the along-
	// slope rafter overhang is 0.2/cos(pitch), not a flat 0.2 m tacked onto
	// the already-projected wall-line run.
	const gableHalfSlopedWWithOverhang =
		(W / 2 + 0.2) / Math.cos(Math.max(pitchRad, 0.0001));
	const averageWallHeight = (wall: "front" | "back" | "left" | "right") =>
		(roofGeometry.getMinWallHeight(wall) +
			roofGeometry.getMaxWallHeight(wall)) /
		2;
	const frontWallHeight = roofGeometry.getMaxWallHeight("front");
	const backWallHeight = roofGeometry.getMaxWallHeight("back");
	const leftWallAverageHeight = averageWallHeight("left");
	const rightWallAverageHeight = averageWallHeight("right");
	const split = resolvePartitionSplit(partitionSplit, L, config.hasRelaxRoom);
	const saunaShare = config.hasRelaxRoom ? split : 1;
	// The glass the model draws: the glazing zones on the panorama wall (split
	// at the partition when there is one), seen between the corner posts. The
	// zones run the outer wall length; the room side of the glass stops a stud
	// depth in from each corner, which is where the interior strips clip it.
	const panoramaInnerStartMm = STUD_D_M * 1000;
	const panoramaInnerEndMm = (L - STUD_D_M) * 1000;
	const panoramaGlassSqm =
		Math.ceil(
			getGlazingZonesForWall(
				TWO_ROOM_PANORAMA_WALL,
				Math.round(L * 1000),
				config,
				split,
			).reduce((sum, zone) => {
				const widthMm =
					Math.min(zone.xEndMm, panoramaInnerEndMm) -
					Math.max(zone.xStartMm, panoramaInnerStartMm);
				return sum + Math.max(0, widthMm) * zone.heightMm;
			}, 0) / 100_000,
		) / 10;
	const framingPlan = calculateTwoRoomFramingPlan(
		W,
		L,
		{
			front: roofConfig.kind === "gable" ? H : frontWallHeight,
			back: roofConfig.kind === "gable" ? H : backWallHeight,
			left: roofConfig.kind === "gable" ? H : roofGeometry.getMaxWallHeight("left"),
			right: roofConfig.kind === "gable" ? H : roofGeometry.getMaxWallHeight("right"),
		},
		openings,
		split,
		config,
    (wall,axisM)=>roofConfig.kind==='gable' ? H : roofGeometry.getRoofHeightAt(wall==='left'?-W/2:wall==='right'?W/2:axisM-W/2,wall==='front'?L/2:wall==='back'?-L/2:axisM-L/2),
	);
	const frontFraming = framingPlan.walls.find((wall) => wall.wall === "front");
	const backFraming = framingPlan.walls.find((wall) => wall.wall === "back");
	const leftFraming = framingPlan.walls.find((wall) => wall.wall === "left");
	const rightFraming = framingPlan.walls.find((wall) => wall.wall === "right");
	// Exterior doors are door openings on a perimeter wall — the divider
	// mellandörr is a separate config/row, so exclude wall === "divider".
	const exteriorDoorCount = openings.filter(
		(opening) => opening.type === "door" && opening.wall !== "divider",
	).length;
	const saunaL = L * split;
	const numFloorJoists = Math.ceil((W - 0.045) / CC) + 1;
	// Trossbottenläkt: one läkt per joist SIDE of every bay, full building
	// length. Mirrors addSubfloorBays, which draws exactly two per bay.
	const lmSubfloorLedger = Math.ceil(resolveSubfloorLedgerLm(W, L, 0.045, CC));
	const numSubfloorLedgers = 2 * resolveSubfloorBayCount(W, 0.045, CC);
	const numRoofJoists = Math.ceil((L - 0.045) / CC) + 1;
	const totalRoofJoists = numRoofJoists;
	const numStudsW = Math.max(
		frontFraming?.standardStudCount ?? 0,
		backFraming?.standardStudCount ?? 0,
	);
	const numStudsL = Math.max(
		leftFraming?.standardStudCount ?? 0,
		rightFraming?.standardStudCount ?? 0,
	);
	const lateralSupportStuds = framingPlan.lateralSupportCount * 2;

	// Gavelfyllnadsreglar
	const numGableInfillStuds =
		roofConfig.kind === "gable"
			? Math.max(0, Math.ceil((W - 0.045) / CC) - 1)
			: 0;
	const gableInfillTotalLm =
		roofConfig.kind === "gable" ? numGableInfillStuds * (roofRise / 2) * 2 : 0;

	const area = W * L;
	const ceilingArea =
		roofConfig.kind === "gable"
			? 2 * ((W / 2 / Math.cos(Math.max(pitchRad, 0.0001))) * L)
			: monoSlopedL * monoEaveL;
	const wallArea =
		roofConfig.kind === "gable"
			? 2 * W * H + W * roofRise + 2 * L * H
			: W * averageWallHeight("front") +
				W * averageWallHeight("back") +
				L * leftWallAverageHeight +
				L * rightWallAverageHeight;
	// The 3D builder panels BOTH faces of the delningsvägg and, on a gable
	// roof, runs them up to the ridge (buildDividerInteriorFromOpenings) — a
	// single flat face under-bought the panel by half. A two-room without
	// relax room builds no divider at all, so it must not be billed either.
	const divWallFaceArea =
		(W - 0.19) * H +
		// Gable and a mono falling across the width both follow the roof
		// across the divider (buildDividerInteriorFromOpenings): mean rise/2.
		(roofConfig.kind === "gable" || monoAcross
			? ((W - 0.19) * roofRise) / 2
			: 0);
	const divWallArea = config.hasRelaxRoom ? 2 * divWallFaceArea : 0;
	const lmWallPanel = Math.ceil((wallArea + divWallArea) * 11.5);
	const lmCeilingPanel = Math.ceil(ceilingArea * 11.5 * 1.1);
	const lmCeilingBattens = Math.ceil(numRoofJoists * W);
	const lmCeilingTrim = Math.ceil(2 * W + 2 * L);
	const foundation = calculateFoundation(W, L, siteConditions);
	const lmFoundationBeams = Math.ceil(foundation.totalBeamLengthLm * 1.05);
	// Gable: buildRoof draws TWO sloped rafters (left + right fall) at every
	// joist position — one half-slope length per position under-bought ~35%.
	const roofJoistLm =
		roofConfig.kind === "gable"
			? totalRoofJoists * 2 * gableHalfSlopedWWithOverhang
			: monoAcross
				? // Rafters sloped across the width, overhang both eaves.
					numRoofJoists * ((W + 0.4) / Math.cos(Math.max(pitchRad, 0.0001)))
				: numRoofJoists * (W + 0.4);
	const lmFloorJoists = Math.ceil(numFloorJoists * L);
	const lmRoofJoists = Math.ceil(roofJoistLm);
	const lateralSupportLm = lateralSupportStuds * H;
	// Divider studs and its syll/hammarband (the W*2 on top of the four
	// perimeter W-plates) only exist when the relax room does.
	const partitionCtx = createTwoRoomBuildContext(
		W,
		L,
		roofConfig,
		roofJoists,
		wallHeightM,
	);
  const partitionZ = -L / 2 + L * (config.saunaPosition === 'right' ? 1-split : split);
  const partitionPlan = config.hasRelaxRoom ? createPartitionFramePlan(W, resolveDividerDoor(partitionCtx, openings),
    x => roofGeometry.getRoofHeightAt(x, partitionZ), roofConfig.kind === 'gable') : undefined;
  const dividerStudLm = partitionPlan?.totalLm ?? 0;
  const dividerPlateLm = 0;
	// For gable: wall studs at constant H on all walls (gavelfyllnadsreglar are separate)
	const lmWallStuds =
		roofConfig.kind === "gable"
			? Math.ceil(
					framingPlan.totalExteriorStudLm +
						framingPlan.extraWallStudLm +
						dividerStudLm +
						lateralSupportLm +
						gableInfillTotalLm +
						W * 4 +
						dividerPlateLm +
						L * 4,
				)
			: Math.ceil(
					framingPlan.totalExteriorStudLm +
						framingPlan.extraWallStudLm +
						dividerStudLm +
						lateralSupportLm +
						// Eave walls: syll + level hammarband; raked walls:
						// syll + hammarband along the slope.
						monoEaveL * 4 +
						dividerPlateLm +
						monoRunL * 2 +
						monoSlopedL * 2,
				);
	const exteriorWallArea = Math.ceil(wallArea);
	const lmExteriorCladding = Math.ceil((exteriorWallArea / ENGINE_CLADDING_COV_M) * 1.1);
	// Ventilated facade layer stack (vindskydd → korsläkt → panel) — shared
	// BOM math so the quantities always mirror what buildExteriorFacade draws.
	// Ströläkt runs on the actual exterior stud lines, which the two-room
	// framing plan varies per wall (panorama mullions, entry variants).
	const studLinesByWall = {
		front: [],
		back: [],
		left: [],
		right: [],
		...Object.fromEntries(
			framingPlan.walls.map((wall) => [wall.wall, wall.studLinePositionsM]),
		),
	} as Record<TwoRoomFramingWall, number[]>;
	const {
		lmStroToLakt,
		lmHorizontalFurring,
		windBarrierArea,
		cornerTrimLm,
		lmBaseTrim,
		baseTrimCutFrontBackM,
		baseTrimCutSideM,
	} = calculateVentilatedFacadeQuantities({
		W,
		L,
		studLinesByWall,
		wallArea,
		roofGeometry,
	});
	// Inv. spikläkt / glespanel. Korsläkt: the battens cross the panel, so the
	// DEFAULT (standing panel) buys LYING rows — rows × span, not studLm —
	// and lying panel flips it to standing runs on the same stud axes the
	// ströläkt uses. The delningsvägg is panelled on both faces, so it
	// contributes two spans of glespanel (see buildDividerBattens).
	// buildTwoRoomInterior returns before buildDividerInteriorFromOpenings when
	// there is no relax room, so a no-relax model must not be billed for the
	// divider's two glespanel faces (~28 lpm on a 3 m model).
	const {
		lmInteriorBattens: lmSpiklakt,
		lmInteriorBattensAlternate: lmSpiklaktAlternate,
	} = calculateInteriorLiningQuantities({
		W,
		L,
		roofGeometry,
		dividerSpans: config.hasRelaxRoom
			? [
					{ lengthM: W - 2 * STUD_D_M, heightM: H },
					{ lengthM: W - 2 * STUD_D_M, heightM: H },
				]
			: undefined,
		panelDirection: finishes.panelDirection,
		studLinesByWall,
	});
	const lmFascia =
		roofConfig.kind === "gable"
			? Math.ceil(2 * (W + 0.45) + 2 * (L + 0.23))
			: Math.ceil(
					2 * (monoEaveL + 0.45) +
						2 * ((monoRunL + 0.23) / Math.cos(Math.max(pitchRad, 0.0001))),
				);
	const subfloorArea = Math.ceil(area);
	const foundationArea = Math.ceil((W + 0.45) * (L + 0.23));
	// Åldersbeständig bastufolie: WALL AREA, not a multiple of the floor area.
	// `saunaL * W * 3` used the plan width W as a stand-in for the wall height,
	// which over-bought ~35 % on the canonical 3.0 m-wide model (W > H) and
	// UNDER-bought on any sauna narrower than 2.1 m.
	//
	// What the 3D actually foils (buildInteriorWallLayers via
	// `vaporBarrierZRange`): with a relax room, the hot room's own end wall
	// (full W, up the roof profile) plus the sauna-length run of both long
	// walls; the relax end wall gets none and the delningsvägg — an interior
	// wall — is never foiled (buildDividerBattens' contract). Without a relax
	// room the band is undefined, so every perimeter wall is foiled. The
	// ceiling folie (buildCeilingVaporBarrier) is never clipped to the band, so
	// the whole ceiling counts either way.
	const saunaEndWall: TwoRoomFramingWall =
		config.saunaPosition === "right" ? "front" : "back";
	// Exact mean height of a linear profile: a gable end wall averages
	// H + roofRise/2 over its triangle, a mono end wall is flat at its own eave.
	const saunaEndWallHeight =
		(roofGeometry.getMinWallHeight(saunaEndWall) +
			roofGeometry.getMaxWallHeight(saunaEndWall)) /
		2;
	// Long walls only carry folie over the sauna band; sample the band's midpoint
	// so a mono side wall contributes its mean height over that band, not the
	// building's.
	const saunaBandMidLocalM =
		config.saunaPosition === "right" ? L - saunaL / 2 : saunaL / 2;
	// Both long walls, each at its own height: a mono falling across the
	// width has one low and one high long wall.
	const saunaSideWallsHeightSum =
		roofGeometry.getWallHeightAt("left", saunaBandMidLocalM) +
		roofGeometry.getWallHeightAt("right", saunaBandMidLocalM);
	const bastufolieWallArea = config.hasRelaxRoom
		? W * saunaEndWallHeight + saunaL * saunaSideWallsHeightSum
		: wallArea;
	const bastufolieArea = Math.ceil(bastufolieWallArea + ceilingArea);
	const foilTapePackages = Math.max(1, Math.ceil(bastufolieArea / 25));
	const fastenerPackages = Math.max(
		2,
		Math.ceil((lmWallStuds + lmFloorJoists + lmRoofJoists) / 120),
	);
	const finishPackages = Math.max(
		1,
		Math.ceil((lmExteriorCladding + lmWallPanel + lmCeilingPanel) / 420),
	);
	const heaterRows: MaterialRow[] =
		heaterType === "electric"
			? [
					{
						material: "Elaggregat",
						dimension: "Mellansegment",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.elaggregat",
						materialKey: "heater.electric",
					},
					{
						material: "Aggregatsten",
						dimension: "20 kg",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.aggregatsten",
						materialKey: "heater.stones",
					},
					{
						material: "Styrpanel",
						dimension: "Extern/integrerad",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.styrpanel",
						materialKey: "heater.control-panel",
					},
				]
			: [
					{
						material: "Bastuaggregat",
						dimension: "Vedeldad kamin",
						quantity: "1 st",
						phase: "systems",
						priceRuleId: "prices.bastuaggregat",
						materialKey: "heater.wood",
					},
				];

	const materials: MaterialRow[] = [
		{
			material: `Betongplint ${foundation.selectedPlinthLengthMm}mm`,
			dimension: "Ø160mm",
			quantity: `${foundation.totalPiers} st`,
			phase: "foundation",
			priceRuleId: "prices.betongplint",
			materialKey: "foundation.plinth",
		},
		{
			material: "Stolpsko / infästning",
			dimension: "Grundinfästning",
			quantity: `${foundation.totalPiers} st`,
			phase: "foundation",
			priceRuleId: "prices.stolpsko",
			materialKey: "foundation.post-shoe",
		},
		{
			material: "Markduk",
			dimension: "Geotextil",
			quantity: `${foundationArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.markduk",
			materialKey: "foundation.ground-fabric",
		},
		{
			material: "Dräneringsgrus / makadam",
			dimension: "Grundlager",
			quantity: `${foundationArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.draneringsgrus",
			materialKey: "foundation.drainage-gravel",
		},
		{
			material: "Bärlina Tryckimpregnerad",
			dimension: "45×145 mm (NTR-A)",
			quantity: `${lmFoundationBeams} lpm`,
			phase: "foundation",
			priceRuleId: "prices.barlina",
			materialKey: "foundation.beam",
		},
		{
			material: "Golvbjälkar (K-virke)",
			dimension: getFloorJoistDimensionLabel(),
			quantity: `${lmFloorJoists} lpm`,
			phase: "framing",
			priceRuleId: getFloorJoistPriceRuleId(),
			materialKey: "framing.floor-joists",
		},
		{
			material: getRoofJoistMaterialName(structuralRoofJoists),
			dimension: getRoofJoistDimensionLabel(structuralRoofJoists),
			quantity: `${lmRoofJoists} lpm`,
			phase: "framing",
			priceRuleId: getRoofJoistPriceRuleId(structuralRoofJoists),
			materialKey: "framing.roof-joists",
		},
		{
			material: "Väggreglar (Ytter+Delning)",
			dimension: "45×95 mm",
			quantity: `${lmWallStuds} lpm`,
			phase: "framing",
			priceRuleId: "prices.vaggreglar",
			materialKey: "framing.wall-studs",
		},
		...framingPlan.extraMaterials,
		{
			material: "Stenullsisolering",
			// The floor bay is joistHeight minus läkt + trossbottenskiva, so the
			// floor batt is a size DOWN from the joist — 145 mm does not fit.
			dimension: `Vägg 95mm / Tak ${Math.round(getRoofJoistDepthM(structuralRoofJoists)*1000)}mm / Golv ${resolveSubfloorBattThicknessMm(JOIST_H)}mm`,
			// The delningsvägg is an interior wall and is never insulated
			// (buildDividerBattens' contract) — exterior walls only here.
			quantity: `Vägg: ${Math.ceil(wallArea)}m² / Tak: ${Math.ceil(ceilingArea)}m² / Golv: ${Math.ceil(area)}m²`,
			phase: "interior",
			priceRuleId: "prices.stenullsisolering",
			materialKey: "interior.insulation",
		},
		{
			material: "Åldersbeständig Bastufolie",
			dimension: "Aluminium",
			quantity: `${bastufolieArea} m²`,
			phase: "interior",
			priceRuleId: "prices.bastufolie",
			materialKey: "interior.vapor-barrier",
		},
		{
			material: "Alutejp / fogtejp",
			dimension: "50 mm",
			quantity: `${foilTapePackages} paket`,
			phase: "interior",
			priceRuleId: "prices.alutejp",
			materialKey: "interior.foil-tape",
		},
		{
			material: "Inv. Spikläkt / Glespanel",
			dimension: "22×45 mm",
			quantity: `${lmSpiklakt} lpm`,
			phase: "interior",
			priceRuleId: "prices.spiklakt",
			materialKey: "interior.batten",
		},
		{
			material: "Takgles / Spikläkt (Innertak)",
			dimension: "22×45 mm",
			quantity: `${lmCeilingBattens} lpm`,
			phase: "interior",
			priceRuleId: "prices.spiklakt",
			materialKey: "interior.ceiling-batten",
		},
		// Without a relax room the whole shell is the bastu: every metre of panel
		// is bastupanel, and there is no relax share to split off.
		{
			material: "Bastupanel (Bastudel)",
			dimension: "15×90 mm",
			quantity: `${Math.ceil(lmWallPanel * saunaShare)} lpm`,
			phase: "interior",
			priceRuleId: "prices.bastupanel",
			materialKey: "interior.sauna-panel",
		},
		...(config.hasRelaxRoom
			? [
					{
						material: "Invändig Panel (Relaxdel)",
						dimension: "15×90 mm",
						quantity: `${Math.ceil(lmWallPanel * (1 - split))} lpm`,
						phase: "interior" as const,
						priceRuleId: "prices.bastupanel",
						materialKey: "interior.relax-panel",
					},
				]
			: []),
		{
			material: "Bastupanel (Innertak bastudel)",
			dimension: "15×90 mm",
			quantity: `${Math.ceil(lmCeilingPanel * saunaShare)} lpm`,
			phase: "interior",
			priceRuleId: "prices.bastupanel",
			materialKey: "interior.ceiling-panel.sauna",
		},
		...(config.hasRelaxRoom
			? [
					{
						material: "Invändig panel (Innertak relaxdel)",
						dimension: "15×90 mm",
						quantity: `${Math.ceil(lmCeilingPanel * (1 - split))} lpm`,
						phase: "interior" as const,
						priceRuleId: "prices.bastupanel",
						materialKey: "interior.ceiling-panel.relax",
					},
				]
			: []),
		{
			material: "Taklist / Bomlist",
			dimension: "22×45 mm",
			quantity: `${lmCeilingTrim} lpm`,
			phase: "interior",
			priceRuleId: "prices.fonsterfoder",
			materialKey: "interior.ceiling-trim",
		},
		// Lavar: layout-backed rows (slats, bärlina, front board, frame, screws).
		...(bench?.materials ?? []),
		{
			// See squareSauna: asfaboard on läkt inside the joist bays, priced on
			// the full floor area because the offcuts at the joist lines are waste.
			material: "Undergolv / Trossbotten",
			dimension: "12 mm asfaboard",
			quantity: `${subfloorArea} m²`,
			phase: "foundation",
			priceRuleId: "prices.undergolv",
			materialKey: "foundation.subfloor",
		},
		{
			material: "Trossbottenläkt",
			dimension: "28×70 mm",
			quantity: `${lmSubfloorLedger} lpm`,
			phase: "foundation",
			priceRuleId: "prices.trossbottenlakt",
			materialKey: "foundation.subfloor-ledger",
		},
		{
			material: "Fukttåligt golv / Trall",
			dimension: "28×120 mm",
			quantity: `${Math.ceil(area)} m²`,
			phase: "interior",
			priceRuleId: "prices.fukttaligtGolv",
			materialKey: "interior.floor",
		},
		{
			material: "Vindskydd (skiva/duk)",
			dimension: "Vindskyddsskiva / diffusionsöppen duk",
			quantity: `${windBarrierArea} m²`,
			phase: "roofExterior",
			priceRuleId: "prices.vindskydd",
			materialKey: "exterior.wind-barrier",
		},
		{
			material: "Ytterpanel (Spontad Gran)",
			dimension: "24×120 mm (110mm täck)",
			quantity: `${lmExteriorCladding} lpm (Inkl. 10% spill)`,
			phase: "roofExterior",
			priceRuleId: "prices.ytterpanel",
			materialKey: "exterior.cladding",
		},
		{
			material: "Utv. Ströläkt (Luftspalt)",
			dimension: "22×45 mm",
			quantity: `${lmStroToLakt} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.spiklakt",
			materialKey: "exterior.furring",
		},
		{
			material: "Utv. Spikläkt (Liggande)",
			dimension: "22×45 mm",
			quantity: `${lmHorizontalFurring} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.spiklakt",
			materialKey: "exterior.furring-horizontal",
		},
		{
			material: "Knutbrädor",
			dimension: "22×90 mm",
			quantity: `${cornerTrimLm} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.knutbrador",
			materialKey: "exterior.trim-boards",
		},
		{
			material: "Nederkantsavslut",
			dimension: "22×95 mm",
			quantity: `${lmBaseTrim} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.nederkantsavslut",
			materialKey: "exterior.drip-edge",
		},
		{
			material: "Vindskivor / Fasadavslut",
			dimension: "22×145 mm",
			quantity: `${lmFascia} lpm`,
			phase: "roofExterior",
			priceRuleId: "prices.vindskivor",
			materialKey: "exterior.fascia",
		},
		{
			material: "Ytterdörr",
			dimension: "Entré / ytterdörr",
			quantity: `${Math.max(1, exteriorDoorCount)} st`,
			phase: "systems",
			priceRuleId: "prices.ytterdorr",
			materialKey: "systems.door.exterior",
		},
		// The mellandörr hangs in the partition, so no partition, no door.
		...(config.hasRelaxRoom
			? [
					{
						material: "Mellendörr",
						dimension: "Invändig glasdörr",
						quantity: "1 st",
						phase: "systems" as const,
						priceRuleId: "prices.mellendorr",
						materialKey: "systems.door.interior",
					},
				]
			: []),
		{
			material: "Fönster",
			dimension: config.hasRelaxRoom ? "Isolerglas relaxdel" : "Isolerglas",
			quantity: "1 st",
			phase: "systems",
			priceRuleId: "prices.fonster",
			materialKey: "systems.window",
		},
		...(panoramaGlassSqm > 0
			? [
					{
						material: "Panoramaglas",
						dimension: "Härdat isolerglas (6H + 6H)",
						quantity: `${panoramaGlassSqm} m²`,
						phase: "systems" as const,
						priceRuleId: "prices.panoramaglas",
						materialKey: "systems.panorama-glass",
					},
				]
			: []),
		{
			material: "Ventiler",
			dimension: "Tilluft + frånluft",
			quantity: "2 st",
			phase: "systems",
			priceRuleId: "prices.ventiler",
			materialKey: "systems.ventilation",
		},
		{
			material: "Rostfri skruv / beslagspaket",
			dimension: "Stomme och invändig infästning",
			quantity: `${fastenerPackages} paket`,
			phase: "systems",
			priceRuleId: "prices.rostfriSkruv",
			materialKey: "systems.fasteners",
		},
		{
			material: "Ytbehandling / färg / olja",
			dimension: "Fasad och utsatta träytor",
			quantity: `${finishPackages} paket`,
			phase: "systems",
			priceRuleId: "prices.ytbehandling",
			materialKey: "systems.surface-treatment",
		},
		...heaterRows,
	];

	const cuts: CutRow[] = [
		{
			part: "Bärlinor (Grund, NTR-A 45×145)",
			count: `${foundation.numBeams} st`,
			cutLength: `${((W - 0.05) * 1000).toFixed(0)} mm`,
			materialKey: "foundation.beam",
		},
		{
			part: "Golvbjälkar (45×145)",
			count: `${numFloorJoists} st`,
			cutLength: `${(L * 1000).toFixed(0)} mm`,
			materialKey: "framing.floor-joist",
		},
		{
			part: `Takbjälkar (${getRoofJoistCutDimensionLabel(structuralRoofJoists)})`,
			count:
				roofConfig.kind === "gable"
					? `${totalRoofJoists * 2} st (inkl. 2 gaveltakstolar)`
					: `${totalRoofJoists} st`,
			cutLength:
				roofConfig.kind === "gable"
					? `${(gableHalfSlopedWWithOverhang * 1000).toFixed(0)} mm per takfall`
					: monoAcross
						? `${(((W + 0.4) / Math.cos(Math.max(pitchRad, 0.0001))) * 1000).toFixed(0)} mm (${roofConfig.pitchDeg}° snitt)`
						: `${((W + 0.4) * 1000).toFixed(0)} mm`,
			...getRoofJoistCutPurchase(structuralRoofJoists),
			materialKey: "framing.roof-joist",
		},
		{
			part: "Syll / Hammarband (Långsida)",
			count: "4 st",
			// A mono falling across the width rakes the front/back walls: their
			// hammarband runs up the slope (W / cos), the syll stays W.
			cutLength: monoAcross
				? `2 × ${(W * 1000).toFixed(0)} mm syll, 2 × ${(monoSlopedL * 1000).toFixed(0)} mm hammarband (${roofConfig.pitchDeg}°)`
				: `${(W * 1000).toFixed(0)} mm`,
			materialKey: "framing.plates",
		},
		{
			part: "Syll / Hammarband (Kortsida)",
			count: "4 st",
			cutLength:
				roofConfig.kind === "gable" || monoAcross
					? `${((L - 0.09) * 1000).toFixed(0)} mm`
					: `2 × ${((L - 0.09) * 1000).toFixed(0)} mm syll, 2 × ${((monoSlopedL - 0.09) * 1000).toFixed(0)} mm hammarband (${roofConfig.pitchDeg}°)`,
			materialKey: "framing.plates-short",
		},
		...(partitionPlan?.cuts ?? []),
		{
			part: "Stående Väggreglar (Framsida)",
			count: `${frontFraming?.standardStudCount ?? numStudsW} st`,
			cutLength:
				roofConfig.kind === "gable"
					? `${((H - 0.09) * 1000).toFixed(0)} mm`
					: monoAcross
						? `${frontFraming?.standardCutLengthsMm.join(" / ")} mm (${roofConfig.pitchDeg}° snitt)`
						: `${((frontWallHeight - 0.09) * 1000).toFixed(0)} mm`,
			materialKey: "framing.studs.front",
 purchaseDimension:"45x95",
 pieces: frontFraming?.standardCutLengthsMm.map(lengthMm=>({lengthMm,count:1})),
		},
		{
			part: "Stående Väggreglar (Baksida)",
			count: `${backFraming?.standardStudCount ?? numStudsW} st`,
			cutLength:
				roofConfig.kind === "gable"
					? `${((H - 0.09) * 1000).toFixed(0)} mm`
					: monoAcross
						? `${backFraming?.standardCutLengthsMm.join(" / ")} mm (${roofConfig.pitchDeg}° snitt)`
						: `${((backWallHeight - 0.09) * 1000).toFixed(0)} mm`,
			materialKey: "framing.studs.back",
 purchaseDimension:"45x95",
 pieces: backFraming?.standardCutLengthsMm.map(lengthMm=>({lengthMm,count:1})),
		},
		{
			part: "Stående Väggreglar (Sidor)",
			count: `${(leftFraming?.standardStudCount ?? numStudsL) + (rightFraming?.standardStudCount ?? numStudsL)} st`,
			cutLength:
				roofConfig.kind === "gable"
					? `${((H - 0.09) * 1000).toFixed(0)} mm`
					: monoAcross
						? `Vänster: ${((roofGeometry.getMaxWallHeight("left") - 0.09) * 1000).toFixed(0)} mm; höger: ${((roofGeometry.getMaxWallHeight("right") - 0.09) * 1000).toFixed(0)} mm`
						: `Vänster: ${leftFraming?.standardCutLengthsMm.join(" / ")} mm; höger: ${rightFraming?.standardCutLengthsMm.join(" / ")} mm (${roofConfig.pitchDeg}° snitt)`,
			materialKey: "framing.studs.sides",
 purchaseDimension:"45x95",
 pieces:[...(leftFraming?.standardCutLengthsMm??[]),...(rightFraming?.standardCutLengthsMm??[])].map(lengthMm=>({lengthMm,count:1})),
		},
		...(lateralSupportStuds > 0
			? [
					{
						part: "Panoramastöd (långvägg)",
						count: `${lateralSupportStuds} st`,
						cutLength: `${((H - 0.09) * 1000).toFixed(0)} mm`,
						materialKey: "framing.panorama-lateral-support",
					},
				]
			: []),
		...framingPlan.extraCuts,
		buildInteriorPanelCutRow({
			lmWallPanel,
			wallHeightM: H,
			// The tallest standing run anywhere on the building — gable ridge or
			// mono high eave. Without it every board is quoted at the flat wall
			// height, and everything above the eave is cut short.
			maxWallHeightM: Math.max(
				...(["front", "back", "left", "right"] as const).map((wall) =>
					roofGeometry.getMaxWallHeight(wall),
				),
			),
			widthM: W,
			lengthM: L,
			finishes,
		}),
		{
			part: "Innertakspanel",
			count: `${Math.ceil(lmCeilingPanel / Math.max(L, 1))} st`,
			cutLength: `Kapas på plats mot ${roofConfig.kind === "gable" ? "takfall / nock" : "taklutning"} (${roofConfig.pitchDeg}°)`,
			materialKey: "interior.ceiling-panel",
		},
		{
			part: "Taklist / Bomlist",
			count: "1 sats",
			cutLength: `Löpande mot vägg/takvinkel, cirka ${lmCeilingTrim} lpm`,
			materialKey: "interior.ceiling-trim",
		},

		{
			part: "Undergolv / Trossbotten",
			count: "1 sats",
			cutLength: `Skivkapas på plats till ${Math.ceil(area)} m²`,
			materialKey: "foundation.subfloor",
		},
		{
			part: "Trossbottenläkt (28×70)",
			count: `${numSubfloorLedgers} st`,
			cutLength: `${(L * 1000).toFixed(0)} mm`,
			materialKey: "foundation.subfloor-ledger",
			purchaseDimension: "28x70",
		},
		{
			part: "Ytterpanel (Fasad)",
			count: `${lmExteriorCladding} lpm`,
			cutLength:
				roofConfig.kind === "gable"
					? "Kapas på plats runt dörr, hörn och gavelspets"
					: `Kapas på plats runt dörr, hörn och pulpettak ${roofConfig.pitchDeg}°`,
			materialKey: "exterior.cladding",
		},
		...(roofConfig.kind === "gable"
			? [
					{
						part: "Gavelfyllnadsreglar (45×95)",
						count: `${numGableInfillStuds * 2} st`,
						cutLength: `Kapas stigande per regel, snedkapas ${roofConfig.pitchDeg}° mot nocken`,
						materialKey: "framing.gable-infill",
					},
				]
			: []),
		{
			part: "Vindskivor / Fasadavslut",
			count: "4 st",
			cutLength:
				roofConfig.kind === "gable"
					? `2 × ${((W + 0.45) * 1000).toFixed(0)} mm, 2 × ${((L + 0.23) * 1000).toFixed(0)} mm`
					: `2 × ${((monoEaveL + 0.45) * 1000).toFixed(0)} mm, 2 × ${(((monoRunL + 0.23) / Math.cos(Math.max(pitchRad, 0.0001))) * 1000).toFixed(0)} mm`,
			materialKey: "exterior.fascia",
		},
		{
			part: "Knutbrädor",
			count: "8 st",
			cutLength: `Kapas på plats mot taklutning (${roofConfig.pitchDeg}°)`,
			materialKey: "exterior.trim-boards",
		},
		{
			part: "Nederkantsavslut",
			count: "4 st",
			cutLength: `2 × ${(baseTrimCutFrontBackM * 1000).toFixed(0)} mm, 2 × ${(baseTrimCutSideM * 1000).toFixed(0)} mm`,
			materialKey: "exterior.drip-edge",
		},
	];

	const partitionWallFraming = calculatePartitionWallFraming({
		dimensions: { width: W, length: L },
		openings,
		partitionSplit,
		roofConfig,
		glazingStyle: config.glazingStyle,
		hasRelaxRoom: config.hasRelaxRoom,
		saunaPosition: config.saunaPosition,
		wallHeightM: H,
	});

	return {
		materials: [...materials, ...partitionWallFraming.materials],
		cuts: [...cuts, ...partitionWallFraming.cuts, ...(bench?.cuts ?? [])],
		foundation,
		partitionWallFraming,
		structuralRoofJoists,
		config,
		finishBasis: {
			facadeAreaSqm: wallArea,
			selectedBattenLpm: lmSpiklakt,
			alternateBattenLpm: lmSpiklaktAlternate,
		},
	};
}

function getTwoRoomCameraPosition(W: number, L: number) {
	const ctx = createTwoRoomBuildContext(W, L);
	return createCameraPosition(
		{
			width: W + 0.45,
			height: ctx.wallBase + ctx.ridgeHeight + 0.08,
			length: L + 0.23,
		},
		[0.57, 0.29, 0.77],
		1.33,
		[0, 0.41, 0],
	);
}

function getTwoRoomCameraPresets(
	input: BuildInput,
	/** Plan-view centre of the hot room's lavar, from the resolved layout. */
	benchFocusM?: { xM: number; zM: number } | null,
) {
	const W = input.dimensions.width;
	const L = input.dimensions.length;
	const ctx = createTwoRoomBuildContext(W, L);
	const size = {
		width: W + 0.45,
		height: ctx.wallBase + ctx.ridgeHeight + 0.08,
		length: L + 0.23,
	};
	const config = resolveTwoRoomConfig(input);

	if (config.entryWall === "long" || config.glazingStyle === "panorama") {
		return [
			createCameraPreset(size, {
				id: "hero",
				label: "Hero",
				direction: [0.92, 0.28, 0.42],
				distanceMultiplier: 1.24,
				targetOffset: [0.08, 0.5, 0],
			}),
			createCameraPreset(size, {
				id: "side",
				label: "Sida",
				direction: [1, 0.16, 0.05],
				distanceMultiplier: 1,
				targetOffset: [0.08, 0.47, 0],
			}),
			createCameraPreset(size, {
				id: "interior-peek",
				label: "Interiör",
				// With a divider, inspect the hot room through the panorama
				// below the fascia. Open layouts use the +Z front cutaway.
				direction: config.hasRelaxRoom ? [0.78, 0.06, 0.34] : [0.27, 0.18, 0.95],
				distanceMultiplier: config.hasRelaxRoom ? 0.98 : 1.16,
				targetOffset: [
					0.04,
					config.hasRelaxRoom ? 0.5 : 0.61,
					config.saunaPosition === "right" ? 0.17 : -0.17,
				],
				targetPlanM: benchFocusM ?? undefined,
			}),
		];
	}

	const hideDivider = config.hasRelaxRoom && config.saunaPosition === "left";
	return [
		createCameraPreset(size, {
			id: "hero",
			label: "Hero",
			direction: [0.51, 0.27, 0.82],
			distanceMultiplier: 1.31,
			targetOffset: [0, 0.5, 0],
		}),
		createCameraPreset(size, {
			id: "side",
			label: "Sida",
			direction: [0.98, 0.15, 0.12],
			distanceMultiplier: 1.02,
			targetOffset: [0, 0.47, 0],
		}),
		{ ...createCameraPreset(size, {
			id: "interior-peek",
			label: "Interiör",
			direction: hideDivider ? [0.1, 0.12, 0.99] : [0.27, 0.18, 0.95],
			distanceMultiplier: hideDivider ? 1.3 : 0.92,
			// Aim at room height: a ridge-height target crops the near floor
			// below the price HUD, with either hot-room placement.
			targetOffset: [0, 0.4, config.saunaPosition === "right" ? 0.17 : -0.17],
			// Frame both exposed rooms in depth, retaining the bench's lateral aim.
			targetPlanM: benchFocusM
				? { ...benchFocusM, zM: hideDivider ? 0 : benchFocusM.zM }
				: undefined,
		}), hideDivider },
	];
}

function createPartitionSubstep(
	partition: NonNullable<CalculationResult["partitionWallFraming"]>,
): BuildGuideSubstepDraft {
	const partitionMaterials = partition.materials.map(
		(row): BuildGuideText => ({
			materialRow: {
				material: row.material,
				dimension: row.dimension,
				quantity: row.quantity,
			},
		}),
	);

	return {
		id: "framing-partition-room-division",
		title: buildGuideText("domain_build_guide_partitionSubstepTitle"),
		materialsSummary:
			partitionMaterials.length > 0
				? partitionMaterials.slice(0, 3)
				: [buildGuideText("domain_build_guide_partitionFallbackMaterials")],
		checkpoints:
			partition.status === "requires-blocking"
				? [
						{
							id: "framing-partition-blocking",
							label: buildGuideText(
								"domain_build_guide_partitionCheckpointBlocking",
								{ count: partition.requiredBlockingCount },
							),
						},
						{
							id: "framing-partition-anchor",
							label: buildGuideText(
								"domain_build_guide_partitionCheckpointAnchor",
							),
						},
					]
				: [
						{
							id: "framing-partition-direct-anchor",
							label: buildGuideText(
								"domain_build_guide_partitionCheckpointDirectAnchor",
							),
						},
					],
	};
}

/**
 * Glass-wall installation substep for panorama variants. Derived entirely from
 * the permit domain's panorama-glass assessment (see the panorama-framing note)
 * — no new geometry or glass calculation happens in the guide layer. Applies to
 * both Lakeside (relax) and Open Panorama (no-relax) panorama designs.
 */
function createGlassWallSubstep(
	panoramaGlass: NonNullable<
		NonNullable<CalculationResult["permit"]>["panoramaGlass"]
	>,
): BuildGuideSubstepDraft {
	const safetyWalls = panoramaGlass.wallAssessments.filter(
		(wall) => wall.requiresSafetyGlass,
	);

	return {
		id: "framing-panorama-glass-wall",
		title: buildGuideText("domain_build_guide_glassWallSubstepTitle"),
		crew: {
			size: "duo",
			reason: buildGuideText("domain_build_guide_glassWallCrewReason"),
		},
		mistakeCallout: {
			severity: "safety",
			wrong: buildGuideText("domain_build_guide_glassWallCalloutWrong"),
			right: buildGuideText("domain_build_guide_glassWallCalloutRight"),
		},
		materialsSummary: [
			buildGuideText("domain_build_guide_glassWallMaterialFallback"),
		],
		checkpoints: [
			{
				id: "panorama-glass-opening",
				label: buildGuideText("domain_build_guide_glassWallCheckpointOpening"),
			},
			{
				id: "panorama-glass-spec",
				label:
					safetyWalls.length > 0
						? buildGuideText(
								"domain_build_guide_glassWallCheckpointSafetyGlass",
								{
									count: safetyWalls.length,
									threshold: panoramaGlass.safetyGlassThresholdSqm,
								},
							)
						: buildGuideText("domain_build_guide_glassWallCheckpointGlassSpec"),
			},
			{
				id: "panorama-glass-seal",
				label: buildGuideText("domain_build_guide_glassWallCheckpointSeal"),
			},
		],
	};
}

function withRelaxCheckpoint(
	substep: BuildGuideSubstepDraft,
	checkpointId: string,
	labelKey: BuildGuideDictionaryKey,
): BuildGuideSubstepDraft {
	return {
		...substep,
		checkpoints: [
			...substep.checkpoints,
			{ id: checkpointId, label: buildGuideText(labelKey) },
		],
	};
}

function modifyTwoRoomBuildGuideSteps(
	steps: ProjectBuildGuideStepDraft[],
	context: BuildGuideModifierContext,
): ProjectBuildGuideStepDraft[] {
	const config = context.data.config;
	const partition = context.data.partitionWallFraming;
	// Open Panorama (no relax room) still owns a glass wall; relax content is
	// pruned but glass-wall content must survive (see the no-relax note).
	const hasRelaxRoom = config?.hasRelaxRoom ?? false;
	const panoramaGlass =
		config?.glazingStyle === "panorama"
			? context.data.permit?.panoramaGlass
			: undefined;

	if (!panoramaGlass && !(hasRelaxRoom && partition)) {
		return steps;
	}

	return steps.map((step) => {
		if (step.id === "framing") {
			// Glass wall lands right after the openings substep; the partition
			// (room division) substep follows it. Both anchor on the openings
			// substep id so a change to the framing substep order can't shuffle
			// them (see insertSubstepsAfterId).
			const extras: BuildGuideSubstepDraft[] = [];
			if (panoramaGlass) {
				extras.push(createGlassWallSubstep(panoramaGlass));
			}
			let substeps = step.substeps;
			if (hasRelaxRoom && partition) {
				// The generic "brace/partition-support" substep is fully covered by
				// the richer room-division substep for two-room, so drop it by id
				// rather than by position and let the partition substep replace it.
				substeps = substeps.filter(
					(substep) => substep.id !== "framing--substep-03",
				);
				extras.push(createPartitionSubstep(partition));
			}
			substeps = insertSubstepsAfterId(substeps, "framing--substep-02", extras);
			return { ...step, substeps };
		}

		// Relax-room reflections: insulation only in the sauna room, ventilation
		// per room, and a separate relax bench. No-relax variants skip all three.
		if (hasRelaxRoom && step.id === "insulation") {
			return {
				...step,
				substeps: step.substeps.map((substep) =>
					substep.id === "insulation--substep-01"
						? withRelaxCheckpoint(
								substep,
								"insulation-sauna-room-only",
								"domain_build_guide_relaxInsulationCheckpoint",
							)
						: substep,
				),
			};
		}

		if (hasRelaxRoom && step.id === "systems") {
			return {
				...step,
				substeps: step.substeps.map((substep) =>
					substep.id === "systems--substep-02"
						? withRelaxCheckpoint(
								substep,
								"systems-vent-per-room",
								"domain_build_guide_relaxVentCheckpoint",
							)
						: substep,
				),
			};
		}

		if (hasRelaxRoom && step.id === "benches") {
			return {
				...step,
				substeps: step.substeps.map((substep) =>
					substep.id === "benches--substep-01"
						? withRelaxCheckpoint(
								substep,
								"benches-relax-room",
								"domain_build_guide_relaxBenchCheckpoint",
							)
						: substep,
				),
			};
		}

		return step;
	});
}

export const twoRoomSaunaEngine: RawSaunaEngine = {
	label: "Bastu med Relaxdel",
	icon: "🏡",
	description: "Fyrkantig bastu med delningsvägg & relaxrum",
	defaultDimensions: TWO_ROOM_DEFAULT_DIMENSIONS,
	buildSauna3D: (input, interior) => buildTwoRoom3D(input, interior),
	calculateMaterials: (input, bench) => {
		const roofJoists = resolveAdvisoryRoofJoists(input);
		const config = resolveTwoRoomConfig(input);
		return {
			...calculateTwoRoomMaterials(
				input.dimensions.width,
				input.dimensions.length,
				input.roofConfig,
				input.siteConditions,
				input.partitionSplit,
				input.openings,
				input.thermalInputs.heaterType,
				roofJoists,
				config,
				bench,
				input.finishes,
				input.wallHeightM,
			),
			roofJoists,
		};
	},
	getAuditMetadata: () => getTwoRoomAuditMetadata(),
	getEvidenceConfig: (input, calculation) =>
		getTwoRoomEvidenceConfig(input, calculation),
	getDefaultCameraPosition: getTwoRoomCameraPosition,
	getCameraPresets: (input, benchFocusM) =>
		getTwoRoomCameraPresets(input, benchFocusM),
	modifyBuildGuideSteps: modifyTwoRoomBuildGuideSteps,
	createBuildContext: (input) =>
		createTwoRoomBuildContext(
			input.dimensions.width,
			input.dimensions.length,
			input.roofConfig,
			getStructuralRoofJoists(resolveAdvisoryRoofJoists(input)),
			input.wallHeightM,
		),
	buildSubsystem: (subsystem, input, ctx, interior) =>
		buildTwoRoomSubsystem(
			subsystem,
			input,
			ctx as SquareBaseBuildContext,
			interior,
		),
};
